import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import AuthScreen from "./AuthScreen";
import { BlessedOpsMindMapDraggable } from "./components/MindMap";
import { lsDeriveKey, lsEncrypt, lsDecrypt } from "./cryptoUtils";

const STORAGE_KEYS = {
  finances:      "finances",
  credit:        "credit",
  tasks:         "tasks",
  learning:      "learning",
  content:       "content",
  journal:       "journal",
  network:       "network",
  quick_logs:    "quick_logs",
  activity_feed: "activity_feed",
  reflections:   "reflections",
  getaways:      "getaways",
  plan:          "plan",
};

const defaultPlan = {
  goals: {
    year:    { text: "", updated: null },
    quarter: { text: "Land $15K-40K bug bounty + 10 Securva customers (90-day challenge Apr 10 - Jul 9)", updated: null },
    month:   { text: "", updated: null },
    week:    { text: "", updated: null },
    today:   { text: "", updated: null },
  },
  todayPlan: { date: null, blocks: [], notes: "", generatedAt: null },
  domains: {
    faith:    { lastTouched: null, label: "Faith / Reflection",   emoji: "🙏" },
    family:   { lastTouched: null, label: "Family / Relationships", emoji: "👨‍👩‍👧" },
    physical: { lastTouched: null, label: "Physical Health",       emoji: "💪" },
    sleep:    { lastTouched: null, label: "Sleep",                 emoji: "😴" },
    reading:  { lastTouched: null, label: "Reading (non-work)",    emoji: "📚" },
    hobbies:  { lastTouched: null, label: "Hobbies / Play",        emoji: "🎨" },
  },
  goalMoves: [],
};

// ============================================================
// DEFAULT DATA
// ============================================================
const defaultFinances = {
  incomeStreams: [
    { id: 1, name: "Day Job", type: "active", monthly: 0, note: "Stable base" },
    { id: 2, name: "BlessedOps Services", type: "semi-passive", monthly: 0, note: "Mini audits, retainers" },
    { id: 3, name: "Gumroad Products", type: "passive", monthly: 0, note: "Templates, guides, checklists" },
    { id: 4, name: "CyberArmor", type: "passive", monthly: 0, note: "Red team kits, courses" },
  ],
  expenses: [],
  monthlyBudget: 0,
  savingsGoal: 0,
  currentSavings: 0,
};

const defaultCredit = {
  currentScore: 0,
  goalScore: 750,
  accounts: [],
  strategies: [
    { id: 1, text: "Keep utilization under 10% on all cards", done: false },
    { id: 2, text: "Never miss a payment — set up autopay minimums", done: false },
    { id: 3, text: "Request CLI every 6 months on existing cards", done: false },
    { id: 4, text: "Apply for a second card once score hits 700+", done: false },
    { id: 5, text: "Build 12+ months of on-time payment history", done: false },
    { id: 6, text: "Check reports annually for errors — dispute anything wrong", done: false },
    { id: 7, text: "Diversify credit mix — installment + revolving", done: false },
    { id: 8, text: "Keep oldest account open — age of credit matters", done: false },
  ],
  history: [],
};

const defaultTasks = {
  items: [
    { id: 1, text: "Pass Security+", status: "active", priority: "high", category: "certification" },
    { id: 2, text: "Post audit offer on socials", status: "pending", priority: "high", category: "business" },
    { id: 3, text: "Record first Intel Drop video", status: "pending", priority: "medium", category: "content" },
    { id: 4, text: "Ship Website Security Checklist to Gumroad", status: "pending", priority: "high", category: "product" },
  ],
};

const defaultLearning = {
  certifications: [
    { id: 1, name: "ISC2 CC", status: "completed", date: "" },
    { id: 2, name: "CompTIA Security+", status: "in-progress", progress: 0, targetDate: "" },
    { id: 3, name: "Next Cert (TBD)", status: "planned", progress: 0, targetDate: "" },
  ],
  skills: [
    { id: 1, name: "Web Recon & Bug Bounty", level: 70 },
    { id: 2, name: "Smart Contract Security", level: 50 },
    { id: 3, name: "GRC / Compliance", level: 40 },
    { id: 4, name: "Content Creation", level: 30 },
    { id: 5, name: "Sales & Client Work", level: 20 },
  ],
};

const defaultContent = {
  posts: [],
  series: [
    { id: 1, name: "Operator Intel Drops", platform: "IG/YT", frequency: "2x/week", active: false },
    { id: 2, name: "Forging the Edge", platform: "IG/YT", frequency: "1x/week", active: false },
  ],
  ideas: [],
};

const defaultJournal = { entries: [], gratitude: [], faithNotes: [] };
const defaultNetwork = { contacts: [], followUps: [], searchTerm: "" };

const defaultGetaways = {
  budgetCap: 200,
  items: [
    // Hiking (Calgary 1-2hr radius, mostly free + park-pass)
    { id: 1,  name: "Ha Ling Peak",                     type: "Hiking",            distance: "1.5hr drive", budget: "Free", cost: 11,  bestFor: "Day trip",    description: "Canmore classic - intermediate scramble with summit ridge views",   saved: false, completed: false, completedDate: null, custom: false },
    { id: 2,  name: "Yamnuska Mountain",                type: "Hiking",            distance: "1hr drive",   budget: "Free", cost: 0,   bestFor: "Day trip",    description: "Bow Valley - intermediate, the iconic cliff face",                  saved: false, completed: false, completedDate: null, custom: false },
    { id: 3,  name: "Heart Creek Trail",                type: "Hiking",            distance: "1hr drive",   budget: "Free", cost: 0,   bestFor: "Few hours",   description: "Easy 4km out-and-back through canyon walls + waterfall",            saved: false, completed: false, completedDate: null, custom: false },
    { id: 4,  name: "Grassi Lakes",                     type: "Hiking",            distance: "1.5hr drive", budget: "Free", cost: 0,   bestFor: "Few hours",   description: "Canmore - easy loop to two turquoise lakes, family-friendly",       saved: false, completed: false, completedDate: null, custom: false },
    { id: 5,  name: "Tunnel Mountain",                  type: "Hiking",            distance: "1.5hr drive", budget: "$",    cost: 11,  bestFor: "Few hours",   description: "Banff - 2hr return, great intro hike + town views",                 saved: false, completed: false, completedDate: null, custom: false },

    // Boating + water (Calgary close, fits $200)
    { id: 6,  name: "Glenmore Reservoir SUP",           type: "Boating",           distance: "20min drive", budget: "$",    cost: 35,  bestFor: "Few hours",   description: "Stand-up paddleboard rental on the city reservoir",                 saved: false, completed: false, completedDate: null, custom: false },
    { id: 7,  name: "Sikome Lake Paddleboard",          type: "Boating",           distance: "30min drive", budget: "$",    cost: 30,  bestFor: "Day trip",    description: "Fish Creek - swim + SUP in summer, full beach setup",               saved: false, completed: false, completedDate: null, custom: false },
    { id: 8,  name: "Spray Lakes Canoe",                type: "Boating",           distance: "1.5hr drive", budget: "$$",   cost: 60,  bestFor: "Day trip",    description: "Canmore - canoe rental on glacier-fed lake, mountain ringed",       saved: false, completed: false, completedDate: null, custom: false },
    { id: 9,  name: "Ghost Lake Kayak",                 type: "Boating",           distance: "45min drive", budget: "$",    cost: 40,  bestFor: "Few hours",   description: "Cochrane - kayak rental, calm reservoir, dog-friendly",             saved: false, completed: false, completedDate: null, custom: false },

    // Adventure (existing + reframed)
    { id: 10, name: "Calgary Climbing Centre",          type: "Adventure",         distance: "Local",        budget: "$",    cost: 25,  bestFor: "Few hours",   description: "Indoor bouldering + top rope - great year-round backup",            saved: false, completed: false, completedDate: null, custom: false },
    { id: 11, name: "Abraham Lake Ice Walk",            type: "Adventure",         distance: "2.5hr drive", budget: "$",    cost: 11,  bestFor: "Day trip",    description: "Winter only - frozen methane bubbles under the Rockies",            saved: false, completed: false, completedDate: null, custom: false },
    { id: 12, name: "Drumheller Badlands",              type: "Adventure",         distance: "1.5hr drive", budget: "$",    cost: 25,  bestFor: "Day trip",    description: "Hoodoos, dinosaurs, and desert vibes - Royal Tyrrell admission",    saved: false, completed: false, completedDate: null, custom: false },

    // Hot Springs + Spa (resets)
    { id: 13, name: "Banff Upper Hot Springs",          type: "Hot Springs",       distance: "1.5hr drive", budget: "$",    cost: 22,  bestFor: "Day trip",    description: "Classic mountain soak with Rockies views",                          saved: false, completed: false, completedDate: null, custom: false },
    { id: 14, name: "Kananaskis Nordic Spa",            type: "Hot Springs",       distance: "1hr drive",   budget: "$$",   cost: 110, bestFor: "Day trip",    description: "Premium hot/cold pools in the forest",                              saved: false, completed: false, completedDate: null, custom: false },

    // Nature Reset
    { id: 15, name: "Lake Louise",                      type: "Nature Reset",      distance: "2hr drive",   budget: "$",    cost: 11,  bestFor: "Day/Weekend", description: "Iconic turquoise lake, peaceful walks",                             saved: false, completed: false, completedDate: null, custom: false },
    { id: 16, name: "Moraine Lake",                     type: "Nature Reset",      distance: "2.5hr drive", budget: "$",    cost: 11,  bestFor: "Day trip",    description: "Valley of the Ten Peaks - early-morning shuttle required",          saved: false, completed: false, completedDate: null, custom: false },
    { id: 17, name: "Canmore Trail Run",                type: "Nature Reset",      distance: "1hr drive",   budget: "Free", cost: 0,   bestFor: "Day trip",    description: "Mountain trails right off the highway",                             saved: false, completed: false, completedDate: null, custom: false },

    // Spiritual + Staycation
    { id: 18, name: "Monastery of the Precious Blood",  type: "Spiritual Retreat", distance: "Local",        budget: "Free", cost: 0,   bestFor: "Few hours",   description: "Silent retreat space, prayer and reflection",                       saved: false, completed: false, completedDate: null, custom: false },
    { id: 19, name: "Home Reset Day",                   type: "Staycation",        distance: "Home",         budget: "Free", cost: 0,   bestFor: "Day",         description: "No screens, journal, stretch, cook a good meal, pray",             saved: false, completed: false, completedDate: null, custom: false },
    { id: 20, name: "Jasper Road Trip",                 type: "Road Trip",         distance: "4hr drive",   budget: "$$",   cost: 180, bestFor: "Weekend",     description: "Icefields Parkway, dark sky preserve, hot springs",                 saved: false, completed: false, completedDate: null, custom: false },

    // Future Self - building toward who you're becoming
    { id: 21, name: "Yamnuska Guided Ice Walk",         type: "Future Self",       distance: "1.5hr drive", budget: "$$",   cost: 130, bestFor: "Day trip",    description: "Pro guide + crampons - the build-confidence-on-real-mountains move", saved: false, completed: false, completedDate: null, custom: false },
    { id: 22, name: "Banff Park Annual Pass",           type: "Future Self",       distance: "Investment",  budget: "$$",   cost: 75,  bestFor: "Year-round",  description: "Unlocks unlimited Banff/Lake Louise/Jasper - pays back at 7 visits", saved: false, completed: false, completedDate: null, custom: false },
    { id: 23, name: "Friends Boat Day",                 type: "Future Self",       distance: "Varies",       budget: "$",    cost: 50,  bestFor: "Day trip",    description: "Round up the squad for a shared rental day - relationship investment", saved: false, completed: false, completedDate: null, custom: false },
  ],
  aiRec: null,
};

// ============================================================
// SCRIPTURES
// ============================================================
const SCRIPTURES = [
  // STRENGTH (25)
  { ref: "Philippians 4:13", text: "I can do all things through Christ who strengthens me.", category: "strength" },
  { ref: "Isaiah 40:31", text: "But those who hope in the Lord will renew their strength. They will soar on wings like eagles; they will run and not grow weary, they will walk and not be faint.", category: "strength" },
  { ref: "Isaiah 41:10", text: "So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with my righteous right hand.", category: "strength" },
  { ref: "Psalm 121:1-2", text: "I lift up my eyes to the mountains — where does my help come from? My help comes from the Lord, the Maker of heaven and earth.", category: "strength" },
  { ref: "Psalm 34:18", text: "The Lord is close to the brokenhearted and saves those who are crushed in spirit.", category: "strength" },
  { ref: "Colossians 3:23", text: "Whatever you do, work at it with all your heart, as working for the Lord, not for human masters.", category: "strength" },
  { ref: "2 Corinthians 12:9", text: "My grace is sufficient for you, for my power is made perfect in weakness.", category: "strength" },
  { ref: "Nehemiah 8:10", text: "Do not grieve, for the joy of the Lord is your strength.", category: "strength" },
  { ref: "Psalm 18:32", text: "It is God who arms me with strength and keeps my way secure.", category: "strength" },
  { ref: "Psalm 46:1", text: "God is our refuge and strength, an ever-present help in trouble.", category: "strength" },
  { ref: "Psalm 28:7", text: "The Lord is my strength and my shield; my heart trusts in him, and he helps me.", category: "strength" },
  { ref: "Habakkuk 3:19", text: "The Sovereign Lord is my strength; he makes my feet like the feet of a deer, he enables me to tread on the heights.", category: "strength" },
  { ref: "Ephesians 6:10", text: "Finally, be strong in the Lord and in his mighty power.", category: "strength" },
  { ref: "1 Corinthians 16:13", text: "Be on your guard; stand firm in the faith; be courageous; be strong.", category: "strength" },
  { ref: "Psalm 73:26", text: "My flesh and my heart may fail, but God is the strength of my heart and my portion forever.", category: "strength" },
  { ref: "Exodus 15:2", text: "The Lord is my strength and my defense; he has become my salvation.", category: "strength" },
  { ref: "Psalm 31:24", text: "Be strong and take heart, all you who hope in the Lord.", category: "strength" },
  { ref: "2 Samuel 22:33", text: "It is God who arms me with strength and keeps my way secure.", category: "strength" },
  { ref: "Psalm 105:4", text: "Look to the Lord and his strength; seek his face always.", category: "strength" },
  { ref: "1 Peter 5:10", text: "And the God of all grace, who called you to his eternal glory in Christ, after you have suffered a little while, will himself restore you and make you strong, firm and steadfast.", category: "strength" },
  { ref: "Romans 8:37", text: "No, in all these things we are more than conquerors through him who loved us.", category: "strength" },
  { ref: "Isaiah 43:2", text: "When you pass through the waters, I will be with you; and when you pass through the rivers, they will not sweep over you.", category: "strength" },
  { ref: "Psalm 62:7", text: "My salvation and my honor depend on God; he is my mighty rock, my refuge.", category: "strength" },
  { ref: "Zephaniah 3:17", text: "The Lord your God is with you, the Mighty Warrior who saves. He will take great delight in you; in his love he will no longer rebuke you, but will rejoice over you with singing.", category: "strength" },
  { ref: "Psalm 27:14", text: "Wait for the Lord; be strong and take heart and wait for the Lord.", category: "strength" },

  // COURAGE (20)
  { ref: "Joshua 1:9", text: "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go.", category: "courage" },
  { ref: "Psalm 27:1", text: "The Lord is my light and my salvation — whom shall I fear? The Lord is the stronghold of my life — of whom shall I be afraid?", category: "courage" },
  { ref: "2 Timothy 1:7", text: "For the Spirit God gave us does not make us timid, but gives us power, love and self-discipline.", category: "courage" },
  { ref: "Deuteronomy 31:6", text: "Be strong and courageous. Do not be afraid or terrified because of them, for the Lord your God goes with you; he will never leave you nor forsake you.", category: "courage" },
  { ref: "Proverbs 18:10", text: "The name of the Lord is a fortified tower; the righteous run to it and are safe.", category: "courage" },
  { ref: "Isaiah 54:4", text: "Do not be afraid; you will not be put to shame. Do not fear disgrace; you will not be humiliated.", category: "courage" },
  { ref: "John 16:33", text: "I have told you these things, so that in me you may have peace. In this world you will have trouble. But take heart! I have overcome the world.", category: "courage" },
  { ref: "Psalm 56:3-4", text: "When I am afraid, I put my trust in you. In God, whose word I praise — in God I trust and am not afraid.", category: "courage" },
  { ref: "1 Chronicles 28:20", text: "Be strong and courageous, and do the work. Do not be afraid or discouraged, for the Lord God, my God, is with you.", category: "courage" },
  { ref: "Romans 8:31", text: "What, then, shall we say in response to these things? If God is for us, who can be against us?", category: "courage" },
  { ref: "Hebrews 13:6", text: "So we say with confidence: The Lord is my helper; I will not be afraid. What can mere mortals do to me?", category: "courage" },
  { ref: "Isaiah 35:4", text: "Say to those with fearful hearts, 'Be strong, do not fear; your God will come, he will come with divine retribution; God will come to save you.'", category: "courage" },
  { ref: "Psalm 118:6", text: "The Lord is with me; I will not be afraid. What can mere mortals do to me?", category: "courage" },
  { ref: "1 John 4:18", text: "There is no fear in love. But perfect love drives out fear, because fear has to do with punishment. The one who fears is not made perfect in love.", category: "courage" },
  { ref: "Numbers 13:30", text: "Then Caleb silenced the people before Moses and said, 'We should go up and take possession of the land, for we can certainly do it.'", category: "courage" },
  { ref: "Acts 4:29", text: "Now, Lord, consider their threats and enable your servants to speak your word with great boldness.", category: "courage" },
  { ref: "Proverbs 28:1", text: "The wicked flee though no one pursues, but the righteous are as bold as a lion.", category: "courage" },
  { ref: "Psalm 3:6", text: "I will not fear though tens of thousands assail me on every side.", category: "courage" },
  { ref: "Hebrews 4:16", text: "Let us then approach God's throne of grace with confidence, so that we may receive mercy and find grace to help us in our time of need.", category: "courage" },
  { ref: "Acts 27:25", text: "So keep up your courage, men, for I have faith in God that it will happen just as he told me.", category: "courage" },

  // PATIENCE (18)
  { ref: "James 1:2-4", text: "Consider it pure joy whenever you face trials of many kinds, because you know that the testing of your faith produces perseverance.", category: "patience" },
  { ref: "Romans 5:3-4", text: "We glory in our sufferings, because we know that suffering produces perseverance; perseverance, character; and character, hope.", category: "patience" },
  { ref: "Galatians 6:9", text: "Let us not become weary in doing good, for at the proper time we will reap a harvest if we do not give up.", category: "patience" },
  { ref: "Romans 8:28", text: "And we know that in all things God works for the good of those who love him, who have been called according to his purpose.", category: "patience" },
  { ref: "Lamentations 3:22-23", text: "Because of the Lord's great love we are not consumed, for his compassions never fail. They are new every morning; great is your faithfulness.", category: "patience" },
  { ref: "Hebrews 12:1", text: "Let us run with perseverance the race marked out for us, fixing our eyes on Jesus, the pioneer and perfecter of faith.", category: "patience" },
  { ref: "James 5:11", text: "As you know, we count as blessed those who have persevered. You have heard of Job's perseverance and have seen what the Lord finally brought about.", category: "patience" },
  { ref: "Psalm 40:1", text: "I waited patiently for the Lord; he turned to me and heard my cry.", category: "patience" },
  { ref: "Isaiah 30:18", text: "Yet the Lord longs to be gracious to you; therefore he will rise up to show you compassion. For the Lord is a God of justice. Blessed are all who wait for him.", category: "patience" },
  { ref: "Ecclesiastes 3:1", text: "There is a time for everything, and a season for every activity under the heavens.", category: "patience" },
  { ref: "2 Peter 3:9", text: "The Lord is not slow in keeping his promise, as some understand slowness. Instead he is patient with you.", category: "patience" },
  { ref: "Psalm 37:7", text: "Be still before the Lord and wait patiently for him; do not fret when people succeed in their ways.", category: "patience" },
  { ref: "Romans 15:4", text: "For everything that was written in the past was written to teach us, so that through the endurance taught in the Scriptures and the encouragement they provide we might have hope.", category: "patience" },
  { ref: "Colossians 1:11", text: "Being strengthened with all power according to his glorious might so that you may have great endurance and patience.", category: "patience" },
  { ref: "Luke 21:19", text: "Stand firm, and you will win life.", category: "patience" },
  { ref: "Revelation 14:12", text: "This calls for patient endurance on the part of the people of God who keep his commands and remain faithful to Jesus.", category: "patience" },
  { ref: "Hebrews 10:36", text: "You need to persevere so that when you have done the will of God, you will receive what he has promised.", category: "patience" },
  { ref: "Micah 7:7", text: "But as for me, I watch in hope for the Lord, I wait for God my Savior; my God will hear me.", category: "patience" },

  // WISDOM (20)
  { ref: "Proverbs 3:5-6", text: "Trust in the Lord with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.", category: "wisdom" },
  { ref: "Proverbs 16:3", text: "Commit to the Lord whatever you do, and he will establish your plans.", category: "wisdom" },
  { ref: "Psalm 16:8", text: "I keep my eyes always on the Lord. With him at my right hand, I will not be shaken.", category: "wisdom" },
  { ref: "Psalm 1:1-3", text: "Blessed is the one whose delight is in the law of the Lord, and who meditates on his law day and night. That person is like a tree planted by streams of water, which yields its fruit in season.", category: "wisdom" },
  { ref: "James 1:5", text: "If any of you lacks wisdom, you should ask God, who gives generously to all without finding fault, and it will be given to you.", category: "wisdom" },
  { ref: "Proverbs 4:7", text: "The beginning of wisdom is this: Get wisdom. Though it cost all you have, get understanding.", category: "wisdom" },
  { ref: "Proverbs 9:10", text: "The fear of the Lord is the beginning of wisdom, and knowledge of the Holy One is understanding.", category: "wisdom" },
  { ref: "Proverbs 11:2", text: "When pride comes, then comes disgrace, but with humility comes wisdom.", category: "wisdom" },
  { ref: "Proverbs 15:22", text: "Plans fail for lack of counsel, but with many advisers they succeed.", category: "wisdom" },
  { ref: "Proverbs 19:20", text: "Listen to advice and accept discipline, and at the end you will be counted among the wise.", category: "wisdom" },
  { ref: "Proverbs 21:5", text: "The plans of the diligent lead to profit as surely as haste leads to poverty.", category: "wisdom" },
  { ref: "Proverbs 24:3-4", text: "By wisdom a house is built, and through understanding it is established; through knowledge its rooms are filled with rare and beautiful treasures.", category: "wisdom" },
  { ref: "Ecclesiastes 7:12", text: "Wisdom is a shelter as money is a shelter, but the advantage of knowledge is this: Wisdom preserves those who have it.", category: "wisdom" },
  { ref: "Psalm 111:10", text: "The fear of the Lord is the beginning of wisdom; all who follow his precepts have good understanding.", category: "wisdom" },
  { ref: "Matthew 7:24", text: "Therefore everyone who hears these words of mine and puts them into practice is like a wise man who built his house on the rock.", category: "wisdom" },
  { ref: "Romans 12:2", text: "Do not conform to the pattern of this world, but be transformed by the renewing of your mind. Then you will be able to test and approve what God's will is.", category: "wisdom" },
  { ref: "Colossians 4:5", text: "Be wise in the way you act toward outsiders; make the most of every opportunity.", category: "wisdom" },
  { ref: "Proverbs 13:20", text: "Walk with the wise and become wise, for a companion of fools suffers harm.", category: "wisdom" },
  { ref: "Proverbs 22:29", text: "Do you see someone skilled in their work? They will serve before kings; they will not serve before officials of low rank.", category: "wisdom" },
  { ref: "Daniel 2:21", text: "He gives wisdom to the wise and knowledge to the discerning.", category: "wisdom" },

  // PROVISION (20)
  { ref: "Jeremiah 29:11", text: "For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.", category: "provision" },
  { ref: "Psalm 23:1", text: "The Lord is my shepherd; I shall not want.", category: "provision" },
  { ref: "Matthew 6:33", text: "But seek first his kingdom and his righteousness, and all these things will be given to you as well.", category: "provision" },
  { ref: "Psalm 37:4", text: "Take delight in the Lord, and he will give you the desires of your heart.", category: "provision" },
  { ref: "Luke 12:32", text: "Do not be afraid, little flock, for your Father has been pleased to give you the kingdom.", category: "provision" },
  { ref: "Philippians 4:19", text: "And my God will meet all your needs according to the riches of his glory in Christ Jesus.", category: "provision" },
  { ref: "Matthew 7:7", text: "Ask and it will be given to you; seek and you will find; knock and the door will be opened to you.", category: "provision" },
  { ref: "Psalm 34:9-10", text: "Fear the Lord, you his holy people, for those who fear him lack nothing. The lions may grow weak and hungry, but those who seek the Lord lack no good thing.", category: "provision" },
  { ref: "Malachi 3:10", text: "Test me in this, says the Lord Almighty, and see if I will not throw open the floodgates of heaven and pour out so much blessing that there will not be room enough to store it.", category: "provision" },
  { ref: "Psalm 84:11", text: "For the Lord God is a sun and shield; the Lord bestows favor and honor; no good thing does he withhold from those whose walk is blameless.", category: "provision" },
  { ref: "2 Corinthians 9:8", text: "And God is able to bless you abundantly, so that in all things at all times, having all that you need, you will abound in every good work.", category: "provision" },
  { ref: "Proverbs 10:22", text: "The blessing of the Lord brings wealth, without painful toil for it.", category: "provision" },
  { ref: "Deuteronomy 8:18", text: "But remember the Lord your God, for it is he who gives you the ability to produce wealth.", category: "provision" },
  { ref: "Psalm 37:25", text: "I was young and now I am old, yet I have never seen the righteous forsaken or their children begging bread.", category: "provision" },
  { ref: "Isaiah 58:11", text: "The Lord will guide you always; he will satisfy your needs in a sun-scorched land and will strengthen your frame.", category: "provision" },
  { ref: "Proverbs 3:9-10", text: "Honor the Lord with your wealth, with the firstfruits of all your crops; then your barns will be filled to overflowing.", category: "provision" },
  { ref: "Romans 8:32", text: "He who did not spare his own Son, but gave him up for us all — how will he not also, along with him, graciously give us all things?", category: "provision" },
  { ref: "John 10:10", text: "The thief comes only to steal and kill and destroy; I have come that they may have life, and have it to the full.", category: "provision" },
  { ref: "3 John 1:2", text: "Dear friend, I pray that you may enjoy good health and that all may go well with you, even as your soul is getting along well.", category: "provision" },
  { ref: "Genesis 22:14", text: "So Abraham called that place The Lord Will Provide. And to this day it is said, 'On the mountain of the Lord it will be provided.'", category: "provision" },

  // GRATITUDE (18)
  { ref: "1 Thessalonians 5:18", text: "Give thanks in all circumstances; for this is God's will for you in Christ Jesus.", category: "gratitude" },
  { ref: "Psalm 103:2", text: "Praise the Lord, my soul, and forget not all his benefits.", category: "gratitude" },
  { ref: "1 Chronicles 29:13", text: "Now, our God, we give you thanks, and praise your glorious name.", category: "gratitude" },
  { ref: "Psalm 107:1", text: "Give thanks to the Lord, for he is good; his love endures forever.", category: "gratitude" },
  { ref: "Psalm 100:4", text: "Enter his gates with thanksgiving and his courts with praise; give thanks to him and praise his name.", category: "gratitude" },
  { ref: "Colossians 3:17", text: "And whatever you do, whether in word or deed, do it all in the name of the Lord Jesus, giving thanks to God the Father through him.", category: "gratitude" },
  { ref: "Psalm 136:1", text: "Give thanks to the Lord, for he is good. His love endures forever.", category: "gratitude" },
  { ref: "Psalm 9:1", text: "I will give thanks to you, Lord, with all my heart; I will tell of all your wonderful deeds.", category: "gratitude" },
  { ref: "Ephesians 5:20", text: "Always giving thanks to God the Father for everything, in the name of our Lord Jesus Christ.", category: "gratitude" },
  { ref: "Psalm 118:24", text: "The Lord has done it this very day; let us rejoice today and be glad.", category: "gratitude" },
  { ref: "Psalm 92:1-2", text: "It is good to praise the Lord and make music to your name, O Most High, proclaiming your love in the morning and your faithfulness at night.", category: "gratitude" },
  { ref: "Hebrews 12:28", text: "Therefore, since we are receiving a kingdom that cannot be shaken, let us be thankful.", category: "gratitude" },
  { ref: "Romans 1:21", text: "Although they knew God, they neither glorified him as God nor gave thanks to him.", category: "gratitude" },
  { ref: "2 Corinthians 4:15", text: "All this is for your benefit, so that the grace that is reaching more and more people may cause thanksgiving to overflow to the glory of God.", category: "gratitude" },
  { ref: "Daniel 6:10", text: "Three times a day he got down on his knees and prayed, giving thanks to his God, just as he had done before.", category: "gratitude" },
  { ref: "Luke 17:15-16", text: "One of them, when he saw he was healed, came back, praising God in a loud voice. He threw himself at Jesus' feet and thanked him.", category: "gratitude" },
  { ref: "Psalm 50:23", text: "Those who sacrifice thank offerings honor me, and to the blameless I will show my salvation.", category: "gratitude" },
  { ref: "Psalm 34:1", text: "I will extol the Lord at all times; his praise will always be on my lips.", category: "gratitude" },

  // REST (18)
  { ref: "Matthew 11:28", text: "Come to me, all you who are weary and burdened, and I will give you rest.", category: "rest" },
  { ref: "Psalm 46:10", text: "Be still, and know that I am God.", category: "rest" },
  { ref: "Philippians 4:6-7", text: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God. And the peace of God will guard your hearts and minds in Christ Jesus.", category: "rest" },
  { ref: "Isaiah 26:3", text: "You will keep in perfect peace those whose minds are steadfast, because they trust in you.", category: "rest" },
  { ref: "Psalm 23:2-3", text: "He makes me lie down in green pastures, he leads me beside quiet waters, he refreshes my soul.", category: "rest" },
  { ref: "Matthew 11:29-30", text: "Take my yoke upon you and learn from me, for I am gentle and humble in heart, and you will find rest for your souls. For my yoke is easy and my burden is light.", category: "rest" },
  { ref: "Psalm 62:5", text: "Yes, my soul, find rest in God; my hope comes from him.", category: "rest" },
  { ref: "John 14:27", text: "Peace I leave with you; my peace I give you. I do not give to you as the world gives. Do not let your hearts be troubled and do not be afraid.", category: "rest" },
  { ref: "Exodus 33:14", text: "The Lord replied, 'My Presence will go with you, and I will give you rest.'", category: "rest" },
  { ref: "1 Kings 19:5", text: "Then he lay down under the bush and fell asleep. All at once an angel touched him and said, 'Get up and eat. The journey is too much for you.'", category: "rest" },
  { ref: "Psalm 4:8", text: "In peace I will lie down and sleep, for you alone, Lord, make me dwell in safety.", category: "rest" },
  { ref: "Isaiah 40:29", text: "He gives strength to the weary and increases the power of the weak.", category: "rest" },
  { ref: "Hebrews 4:9-10", text: "There remains, then, a Sabbath-rest for the people of God; for anyone who enters God's rest also rests from their works, just as God did from his.", category: "rest" },
  { ref: "Psalm 116:7", text: "Return to your rest, my soul, for the Lord has been good to you.", category: "rest" },
  { ref: "Mark 6:31", text: "Then, because so many people were coming and going that they did not even have a chance to eat, he said to them, 'Come with me by yourselves to a quiet place and get some rest.'", category: "rest" },
  { ref: "Jeremiah 6:16", text: "Stand at the crossroads and look; ask for the ancient paths, ask where the good way is, and walk in it, and you will find rest for your souls.", category: "rest" },
  { ref: "Psalm 131:2", text: "But I have calmed and quieted myself, I am like a weaned child with its mother; like a weaned child I am content.", category: "rest" },
  { ref: "Romans 15:13", text: "May the God of hope fill you with all joy and peace as you trust in him, so that you may overflow with hope by the power of the Holy Spirit.", category: "rest" },
];

const getScripture = (category) => {
  const pool = category ? SCRIPTURES.filter(s => s.category === category) : SCRIPTURES;
  return pool[Math.floor(Math.random() * pool.length)];
};

const getEncouragementScripture = () => {
  const cats = ["strength", "courage", "patience"];
  return getScripture(cats[Math.floor(Math.random() * cats.length)]);
};

// ============================================================
// STORAGE HOOK - Supabase primary, localStorage fallback/cache
// ============================================================
// One-time migration: move data from old localStorage keys to new table-name keys
const LS_MIGRATION_MAP = {
  "blessedops-finances-v1":      "finances",
  "blessedops-credit-v1":        "credit",
  "blessedops-tasks-v1":         "tasks",
  "blessedops-learning-v1":      "learning",
  "blessedops-content-v1":       "content",
  "blessedops-journal-v1":       "journal",
  "blessedops-network-v1":       "network",
  "blessedops-quicklogs-v1":     "quick_logs",
  "blessedops-activity-feed-v1": "activity_feed",
  "blessedops-reflections-v1":   "reflections",
  "blessedops-getaways-v1":      "getaways",
};
(function migrateLS() {
  try {
    Object.entries(LS_MIGRATION_MAP).forEach(([oldKey, newKey]) => {
      const val = localStorage.getItem(oldKey);
      if (val !== null && localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, val);
      }
      localStorage.removeItem(oldKey);
    });
  } catch (_) {}
})();

// ============================================================
// useStorage — Supabase-first, AES-encrypted localStorage cache
// ============================================================
function useStorage(key, defaultValue) {
  const [data, setData] = useState(defaultValue);
  const [loaded, setLoaded] = useState(false);
  const debounceRef = useRef(null);
  const cryptoKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) { setLoaded(true); return; }
      const userId = session.user.id;

      // Derive encryption key for this user
      try { cryptoKeyRef.current = await lsDeriveKey(userId); } catch (_) {}

      try {
        const { data: row } = await supabase
          .from(key)
          .select('data')
          .eq('user_id', userId)
          .maybeSingle();
        if (cancelled) return;
        if (row?.data !== undefined && row?.data !== null) {
          setData(row.data);
          // Write encrypted cache
          if (cryptoKeyRef.current) {
            lsEncrypt(cryptoKeyRef.current, row.data)
              .then(enc => { try { localStorage.setItem(key, enc); } catch (_) {} })
              .catch(() => {});
          }
        } else {
          // No Supabase row — try decrypting localStorage cache to seed Supabase
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              let localData;
              try {
                // Try decrypted first, fall back to plaintext JSON (migration)
                localData = cryptoKeyRef.current
                  ? await lsDecrypt(cryptoKeyRef.current, cached)
                  : JSON.parse(cached);
              } catch (_) {
                try { localData = JSON.parse(cached); } catch (_2) {}
              }
              if (!cancelled && localData) {
                setData(localData);
                await supabase
                  .from(key)
                  .upsert({ user_id: userId, data: localData, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
              }
            }
          } catch (_) {}
        }
      } catch (e) {
        console.warn(`Supabase load failed for ${key}:`, e?.message || e);
      }
      if (!cancelled) setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line

  const save = useCallback((newData) => {
    setData(newData);
    // Write encrypted to localStorage
    if (cryptoKeyRef.current) {
      lsEncrypt(cryptoKeyRef.current, newData)
        .then(enc => { try { localStorage.setItem(key, enc); } catch (_) {} })
        .catch(() => { try { localStorage.setItem(key, JSON.stringify(newData)); } catch (_) {} });
    } else {
      try { localStorage.setItem(key, JSON.stringify(newData)); } catch (_) {}
    }
    // Debounce Supabase write by 1 second
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await supabase
        .from(key)
        .upsert({ user_id: session.user.id, data: newData, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    }, 1000);
  }, [key]);

  return [data, save, loaded];
}

// ============================================================
// QUICK LOG OVERLAY  — module level to prevent iOS keyboard kill
// ============================================================
function QuickLogOverlay({ isOpen, onClose, quickLogs, setQuickLogs }) {
  const [text, setText] = React.useState("");
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) { setText(""); setSaved(false); }
  }, [isOpen]);

  const handleLog = () => {
    if (!text.trim()) return;
    const now = new Date();
    const entry = { id: Date.now(), text: text.trim(), timestamp: now.toISOString(), date: now.toISOString().split("T")[0] };
    setQuickLogs([entry, ...(quickLogs || [])]);
    setSaved(true);
    setTimeout(onClose, 900);
  };

  if (!isOpen) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 150 }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} onClick={onClose} />
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "#13151A", borderRadius: "16px 16px 0 0",
        border: "1px solid #2A2E38", borderBottom: "none",
        padding: `20px 20px calc(24px + env(safe-area-inset-bottom, 0px))`,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#00E5A0", letterSpacing: 0.5 }}>⚡ Quick Log</div>
          <div style={{ fontSize: 11, color: "#3A3E4A" }}>
            {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </div>
        </div>
        {saved ? (
          <div style={{ textAlign: "center", padding: "18px 0", fontSize: 16, color: "#00E5A0", fontWeight: 700 }}>Logged ✓</div>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              autoFocus
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleLog()}
              placeholder="What's happening right now..."
              style={{
                flex: 1, background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 10,
                color: "#E8E9EC", padding: "13px 14px", fontSize: 15, outline: "none", fontFamily: "inherit",
              }}
            />
            <button
              onClick={handleLog}
              style={{
                background: text.trim() ? "linear-gradient(135deg, #00E5A0, #7C8CF8)" : "#1A1D23",
                border: "none", borderRadius: 10, color: text.trim() ? "#0B0D11" : "#6B7080",
                padding: "0 22px", fontWeight: 700, fontSize: 14, cursor: text.trim() ? "pointer" : "default", minWidth: 68,
              }}
            >Log</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ACTIVITY FEED — module-level helpers + overlay
// ============================================================
const SOURCE_COLORS = {
  "Claude Session":       "#7C8CF8",
  "Bug Bounty":           "#FF6B6B",
  "Study/Certs":          "#FFD166",
  "Workout/Health":       "#00E5A0",
  "Business/BlessedOps":  "#00E5A0",
  "Content Creation":     "#FF8C66",
  "Faith":                "#E8A0FF",
  "Personal":             "#6B7080",
  "Other":                "#6B7080",
};
const ALL_SOURCES = Object.keys(SOURCE_COLORS);

function ActivitySummaryOverlay({ isOpen, onClose, activityFeed, setActivityFeed }) {
  const [title, setTitle] = React.useState("");
  const [text, setText] = React.useState("");
  const [source, setSource] = React.useState("Other");
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) { setTitle(""); setText(""); setSource("Other"); setSaved(false); }
  }, [isOpen]);

  const handleSave = () => {
    if (!text.trim()) return;
    const now = new Date();
    const entry = { id: Date.now(), title: title.trim(), text: text.trim(), source, timestamp: now.toISOString(), date: now.toISOString().split("T")[0] };
    setActivityFeed([entry, ...(activityFeed || [])]);
    setSaved(true);
    setTimeout(onClose, 1000);
  };

  if (!isOpen) return null;
  const accentColor = SOURCE_COLORS[source] || "#6B7080";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 150 }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} onClick={onClose} />
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "#13151A", borderRadius: "16px 16px 0 0",
        border: "1px solid #2A2E38", borderBottom: "none",
        padding: `20px 20px calc(24px + env(safe-area-inset-bottom, 0px))`,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        {saved ? (
          <div style={{ textAlign: "center", padding: "24px 0", fontSize: 16, color: "#00E5A0", fontWeight: 700 }}>Saved to Activity Feed ✓</div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#7C8CF8" }}>Activity Summary</div>
              <div style={{ fontSize: 11, color: "#3A3E4A" }}>
                {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </div>
            </div>
            {/* Source selector */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Source</div>
              <select
                value={source}
                onChange={e => setSource(e.target.value)}
                style={{ width: "100%", background: "#1A1D23", border: `1px solid ${accentColor}44`, borderRadius: 8, color: accentColor, padding: "9px 12px", fontSize: 13, outline: "none", fontFamily: "inherit" }}
              >
                {ALL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* Optional title */}
            <div style={{ marginBottom: 12 }}>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Title (optional)"
                style={{ width: "100%", background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "9px 12px", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
            </div>
            {/* Main text area */}
            <div style={{ marginBottom: 14 }}>
              <textarea
                autoFocus
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Paste a summary, key takeaways, notes from a session..."
                rows={5}
                style={{ width: "100%", background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
              />
            </div>
            <button
              onClick={handleSave}
              style={{
                width: "100%", padding: "13px", borderRadius: 10, border: "none",
                background: text.trim() ? `linear-gradient(135deg, ${accentColor}33, ${accentColor}22)` : "#1A1D23",
                border: `1px solid ${text.trim() ? accentColor + "55" : "#2A2E38"}`,
                color: text.trim() ? accentColor : "#6B7080",
                fontSize: 14, fontWeight: 700, cursor: text.trim() ? "pointer" : "default",
              }}
            >Save to Feed</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ICONS
// ============================================================
const Icons = {
  Dashboard: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  Money: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  Credit: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  Tasks: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  Learn: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>,
  Content: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
  Faith: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
  Network: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  AI: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Profile: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  Activity: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  Getaway: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  Plus: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Trash: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
  Check: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Arrow: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  Send: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  Zap: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
};

// ============================================================
// DEFERRED INPUT - prevents keyboard dismissal on mobile
// ============================================================
const DeferredInput = ({ value, onCommit, style, placeholder, type = "text", ...props }) => {
  const [local, setLocal] = useState(value);
  const committedRef = useRef(value);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current && value !== committedRef.current) {
      setLocal(value);
      committedRef.current = value;
    }
  }, [value]);

  return (
    <input
      {...props}
      type={type}
      value={local}
      placeholder={placeholder}
      style={style}
      onChange={e => setLocal(e.target.value)}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={() => {
        focusedRef.current = false;
        if (local !== committedRef.current) {
          committedRef.current = local;
          onCommit(local);
        }
      }}
      onKeyDown={e => { if (e.key === "Enter") { e.target.blur(); } }}
    />
  );
};

// ============================================================
// SHARED UI
// ============================================================
const ProgressBar = ({ value, max = 100, color = "#00E5A0", height = 8, label }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ width: "100%" }}>
      {label && <div style={{ fontSize: 11, color: "#8A8F98", marginBottom: 4, display: "flex", justifyContent: "space-between" }}><span>{label}</span><span>{Math.round(pct)}%</span></div>}
      <div style={{ width: "100%", height, background: "#1A1D23", borderRadius: height / 2 }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: height / 2, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, color = "#00E5A0", icon }) => (
  <div className="bo-stat-card" style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: "14px 16px", flex: "1 1 calc(50% - 6px)", minWidth: 0 }}>
    <div style={{ fontSize: 10, color: "#6B7080", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, display: "flex", alignItems: "center", gap: 5, overflow: "hidden", whiteSpace: "nowrap" }}>{icon} {label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#6B7080", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
  </div>
);

const SectionHeader = ({ title, onAdd, addLabel = "Add" }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
    <h3 style={{ fontSize: 16, fontWeight: 600, color: "#E8E9EC", margin: 0 }}>{title}</h3>
    {onAdd && <button onClick={onAdd} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(0,229,160,0.1)", border: "1px solid rgba(0,229,160,0.2)", color: "#00E5A0", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}><Icons.Plus /> {addLabel}</button>}
  </div>
);

// ============================================================
// MAIN APP
// ============================================================
export function AppWrapper() {
  const [session, setSession] = useState(undefined);
  const [isRecovery, setIsRecovery] = useState(false);
  const recoveryRef = useRef(false);
  // If ?code= is in the URL, Supabase is processing an auth flow — ignore INITIAL_SESSION
  const hasCodeRef = useRef(new URLSearchParams(window.location.search).has('code'));

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        // If a ?code= param exists, ignore this — the real event (PASSWORD_RECOVERY or SIGNED_IN)
        // is about to fire once Supabase finishes the PKCE exchange
        if (!hasCodeRef.current) setSession(session);
        return;
      }
      if (event === 'PASSWORD_RECOVERY') {
        hasCodeRef.current = false;
        recoveryRef.current = true;
        setIsRecovery(true);
        setSession(null);
      } else if (event === 'SIGNED_IN' && recoveryRef.current) {
        // Suppress SIGNED_IN that fires immediately after PASSWORD_RECOVERY
        return;
      } else {
        hasCodeRef.current = false;
        recoveryRef.current = false;
        setIsRecovery(false);
        setSession(session);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    return (
      <div style={{ background: "#0B0D11", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono', monospace" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#00E5A0", marginBottom: 8 }}>BLESSEDOPS</div>
          <div style={{ fontSize: 13, color: "#6B7080" }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!session || isRecovery) return <AuthScreen isRecovery={isRecovery} />;
  return <BlessedOpsCommandCenter session={session} />;
}

export default AppWrapper;

function BlessedOpsCommandCenter({ session }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [finances, setFinances, finLoaded] = useStorage(STORAGE_KEYS.finances, defaultFinances);
  const [credit, setCredit, credLoaded] = useStorage(STORAGE_KEYS.credit, defaultCredit);
  const [tasks, setTasks, taskLoaded] = useStorage(STORAGE_KEYS.tasks, defaultTasks);
  const [learning, setLearning, learnLoaded] = useStorage(STORAGE_KEYS.learning, defaultLearning);
  const [content, setContent, contLoaded] = useStorage(STORAGE_KEYS.content, defaultContent);
  const [journal, setJournal, jourLoaded] = useStorage(STORAGE_KEYS.journal, defaultJournal);
  const [network, setNetwork, netLoaded] = useStorage(STORAGE_KEYS.network, defaultNetwork);
  const [quickLogs, setQuickLogs, qlLoaded] = useStorage(STORAGE_KEYS.quick_logs, []);
  const [activityFeed, setActivityFeed, afLoaded] = useStorage(STORAGE_KEYS.activity_feed, []);
  const [reflections, setReflections, reflLoaded] = useStorage(STORAGE_KEYS.reflections, []);
  const [getaways, setGetaways, getawaysLoaded] = useStorage(STORAGE_KEYS.getaways, defaultGetaways);
  const [plan, setPlan, planLoaded] = useStorage(STORAGE_KEYS.plan, defaultPlan);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showMindMap, setShowMindMap] = useState(false);
  const [showQuickLog, setShowQuickLog] = useState(false);
  const [showActivitySummary, setShowActivitySummary] = useState(false);
  const [quickActionMenu, setQuickActionMenu] = useState(false);

  const allLoaded = finLoaded && credLoaded && taskLoaded && learnLoaded && contLoaded && jourLoaded && netLoaded && qlLoaded && afLoaded && reflLoaded && getawaysLoaded && planLoaded;

  const tabs = [
    { id: "dashboard", label: "Command Center", icon: <Icons.Dashboard /> },
    { id: "plan", label: "Day Plan + Goals", icon: <Icons.Tasks /> },
    { id: "advisor", label: "AI Advisor", icon: <Icons.AI /> },
    { id: "activity", label: "Activity Feed", icon: <Icons.Activity /> },
    { id: "money", label: "Money & Revenue", icon: <Icons.Money /> },
    { id: "credit", label: "Credit Building", icon: <Icons.Credit /> },
    { id: "tasks", label: "Tasks & Projects", icon: <Icons.Tasks /> },
    { id: "learning", label: "Learning & Certs", icon: <Icons.Learn /> },
    { id: "content", label: "Content Pipeline", icon: <Icons.Content /> },
    { id: "getaways", label: "Getaways", icon: <Icons.Getaway /> },
    { id: "faith", label: "Faith & Journal", icon: <Icons.Faith /> },
    { id: "network", label: "Network & CRM", icon: <Icons.Network /> },
    { id: "profile", label: "Account & Profile", icon: <Icons.Profile /> },
  ];

  if (!allLoaded) {
    return (
      <div style={{ background: "#0B0D11", color: "#E8E9EC", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono', monospace" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#00E5A0", marginBottom: 8 }}>BLESSEDOPS</div>
          <div style={{ fontSize: 13, color: "#6B7080" }}>Loading command center...</div>
        </div>
      </div>
    );
  }

  const totalMonthlyIncome = finances.incomeStreams.reduce((s, i) => s + (Number(i.monthly) || 0), 0);
  const passiveIncome = finances.incomeStreams.filter(i => i.type !== "active").reduce((s, i) => s + (Number(i.monthly) || 0), 0);
  const activeTasks = tasks.items.filter(t => t.status === "active").length;
  const completedTasks = tasks.items.filter(t => t.status === "done").length;
  const pendingTasks = tasks.items.filter(t => t.status === "pending").length;
  const creditScore = Number(credit.currentScore) || 0;
  const creditGoal = Number(credit.goalScore) || 750;
  const strategiesDone = credit.strategies.filter(s => s.done).length;

  const getAllData = () => {
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
    const recentFeed = activityFeed.filter(e => e.date >= weekAgo).slice(0, 30).map(e => ({
      source: e.source, title: e.title, text: e.text.slice(0, 300), date: e.date,
    }));
    const recentQLogs = quickLogs.filter(l => l.date >= weekAgo).slice(0, 30);
    return {
      finances: { ...finances, totalMonthlyIncome, passiveIncome },
      credit: { ...credit, totalLimit: credit.accounts.reduce((s, a) => s + (Number(a.limit) || 0), 0), totalBalance: credit.accounts.reduce((s, a) => s + (Number(a.balance) || 0), 0), strategiesCompleted: strategiesDone, strategiesTotal: credit.strategies.length },
      tasks: { ...tasks, activeTasks, pendingTasks, completedTasks },
      learning, content,
      journal: { entriesCount: journal.entries.length, gratitudeCount: journal.gratitude.length, faithNotesCount: journal.faithNotes.length, recentEntries: journal.entries.slice(0, 3) },
      network: { contactsCount: network.contacts.length },
      activityFeed: { recentEntries: recentFeed, totalEntries: activityFeed.length },
      quickLogs: { recentEntries: recentQLogs, totalEntries: quickLogs.length },
    };
  };

  // ============================================================
  // AI ADVISOR
  // ============================================================
  const AdvisorView = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedFocus, setSelectedFocus] = useState(null);

    const focusAreas = [
      { id: "credit", label: "Credit Strategy", icon: <Icons.Credit />, color: "#E8A0FF", prompt: "Analyze my current credit situation and give me the exact next 3 moves to get to 750+. Be specific based on my accounts, utilization, and score history." },
      { id: "income", label: "Income Growth", icon: <Icons.Money />, color: "#00E5A0", prompt: "Look at my current income streams and tell me exactly what to activate first, what's highest leverage, and give me a 30-day action plan to increase revenue." },
      { id: "tasks", label: "Priority Check", icon: <Icons.Tasks />, color: "#7C8CF8", prompt: "Review all my pending and active tasks. Tell me what I should focus on THIS WEEK, what can wait, and what I should drop entirely. Be ruthless about prioritization." },
      { id: "full", label: "Full Operator Brief", icon: <Icons.Dashboard />, color: "#FFD166", prompt: "Give me a full operational briefing. Look at everything — income, credit, tasks, learning, content — and tell me: what's working, what's stalled, and what are the top 3 highest-leverage moves I should make in the next 7 days." },
      { id: "content", label: "Content Strategy", icon: <Icons.Content />, color: "#FF8C66", prompt: "Analyze my content pipeline and series status. Tell me what content to create this week that will drive the most traffic to my offers and products. Give me specific topics and hooks." },
      { id: "learning", label: "Skill Roadmap", icon: <Icons.Learn />, color: "#4ECDC4", prompt: "Look at my certifications and skill levels. What should I be studying right now, what's the most valuable next cert after Security+, and how do I turn my current skills into more revenue?" },
    ];

    const runAdvisor = async (prompt) => {
      setLoading(true);
      const data = getAllData();
      const systemPrompt = `You are the BlessedOps AI Advisor — a strategic operations analyst embedded inside the BlessedOps Command Center. You have full visibility into the operator's data across all systems.

OPERATOR CONTEXT:
- Building BlessedOps: cybersecurity-driven digital products, services, and content
- Works full-time physical job, builds on the margins
- Has ISC2 CC completed, Security+ in progress
- Runs mini audit services for SMBs, Gumroad store with digital products
- Content series: Operator Intel Drops + Forging the Edge
- Faith-driven, systems-over-hustle philosophy
- Aiming for 750+ credit score to unlock premium cards/LOCs
- Currently has 1-2 credit cards, wants to level up strategically

CURRENT DATA SNAPSHOT:
${JSON.stringify({ ...data, activityFeed: undefined, quickLogs: undefined }, null, 2)}

RECENT ACTIVITY FEED — last 7 days (${data.activityFeed.totalEntries} total entries):
${data.activityFeed.recentEntries.length ? data.activityFeed.recentEntries.map(e => `[${e.date}] [${e.source}]${e.title ? " " + e.title + ":" : ""} ${e.text}`).join("\n") : "No activity logged yet — encourage the operator to log their sessions."}

RECENT QUICK LOGS — last 7 days:
${data.quickLogs.recentEntries.length ? data.quickLogs.recentEntries.map(l => `[${l.date} ${new Date(l.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}] ${l.text}`).join("\n") : "None"}

STRATEGIC FRAMEWORKS (apply these principles naturally in your advice):

NETWORKING — "How to Win Friends and Influence People" (Carnegie):
- Lead with genuine interest in the other person before pitching anything
- Remember and use people's names — it matters more than most think
- Make others feel important; listen more than you talk
- Talk in terms of their interests, not yours
- When reconnecting after time away, ask about their work first

FINANCE — "The Richest Man in Babylon" (Clason):
- Pay yourself first — at least 10% of every dollar earned goes to wealth-building
- Make money work for you; idle money loses to inflation
- Guard against loss before chasing gain — the first rule of wealth is don't lose it
- Seek counsel only from those competent in the area you're asking about

BUSINESS — "The Lean Startup" (Ries):
- Build-Measure-Learn loop: ship the smallest version, get feedback, iterate
- Validated learning beats assumed progress — are users actually paying?
- Pivot or persevere decisions must be based on data, not feelings
- An MVP that generates feedback is worth more than a perfect product no one has seen

PERSONAL DEVELOPMENT — "Atomic Habits" (Clear):
- 1% improvements compound — don't underestimate small daily actions
- Habit stacking: attach new behaviors to existing ones
- Environment design beats willpower — make the right action the easy action
- Identity-based habits stick: "I am someone who ships weekly" vs "I want to ship more"

FAITH & LEADERSHIP — "Disciplines of a Godly Man" (Hughes):
- Stewardship: every gift, skill, and opportunity is entrusted, not owned
- Consistency in the unseen disciplines builds character for the public moments
- Servant leadership — authority flows to those who serve, not those who grab

SALES & INFLUENCE — "Never Split the Difference" (Voss):
- Tactical empathy: name what the other person is feeling before negotiating
- Mirroring: repeat the last 3 words someone said — it opens them up
- Labeling: "It sounds like..." acknowledges without agreeing
- Calibrated questions ("How am I supposed to do that?") create problem-solving partnerships

RULES:
- Be direct, tactical, and specific. No fluff.
- Give numbered action items with clear next steps
- Reference specific data points from their dashboard
- Think like a Chief of Staff giving a daily briefing
- Weave in relevant book principles naturally — don't force them, use them when they sharpen the advice
- If data is missing (zeros, empty), call it out and tell them what to input
- Always end with "NEXT ACTION:" — one single thing to do right now
- Keep responses focused and actionable, not longer than needed
- Use plain text formatting only, no markdown`;

      const newMessages = [...messages, { role: "user", content: prompt, timestamp: new Date().toLocaleTimeString() }];
      setMessages(newMessages);

      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const response = await fetch("/api/claude", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${s?.access_token}` },
          body: JSON.stringify({
            model: "claude-sonnet-4-20250514",
            max_tokens: 1000,
            system: systemPrompt,
            messages: [{ role: "user", content: prompt }],
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "API error");
        const assistantText = result.content?.map(c => c.text || "").join("\n") || "Unable to generate analysis. Try again.";
        setMessages(prev => [...prev, { role: "assistant", content: assistantText, timestamp: new Date().toLocaleTimeString() }]);
      } catch (error) {
        setMessages(prev => [...prev, { role: "assistant", content: "Connection error — check your internet and try again.", timestamp: new Date().toLocaleTimeString() }]);
      }
      setLoading(false);
      setInput("");
    };

    return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>
            <span style={{ background: "linear-gradient(135deg, #00E5A0, #7C8CF8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>AI Advisor</span>
          </h2>
          <p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Strategic analysis powered by your real-time data</p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, color: "#6B7080", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>Quick Analysis</div>
          <div className="bo-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {focusAreas.map(f => (
              <button key={f.id} onClick={() => { setSelectedFocus(f.id); runAdvisor(f.prompt); }} disabled={loading} style={{ background: selectedFocus === f.id ? `${f.color}15` : "#13151A", border: `1px solid ${selectedFocus === f.id ? `${f.color}40` : "#1E2028"}`, borderRadius: 10, padding: "14px 16px", cursor: loading ? "not-allowed" : "pointer", textAlign: "left", transition: "all 0.2s ease", opacity: loading ? 0.6 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ color: f.color }}>{f.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#E8E9EC" }}>{f.label}</span>
                </div>
                <div style={{ fontSize: 11, color: "#6B7080", lineHeight: 1.4 }}>
                  {f.id === "credit" && "Next moves to 750+"}
                  {f.id === "income" && "What to activate now"}
                  {f.id === "tasks" && "This week's priorities"}
                  {f.id === "full" && "Complete operational brief"}
                  {f.id === "content" && "What to post this week"}
                  {f.id === "learning" && "Cert & skill roadmap"}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 16, marginBottom: 20, display: "flex", gap: 10, alignItems: "center" }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !loading && input.trim() && runAdvisor(input)} placeholder="Ask anything about your operation..." disabled={loading} style={{ flex: 1, background: "#0B0D11", border: "1px solid #3A3E4A", borderRadius: 8, color: "#E8E9EC", padding: "12px 16px", fontSize: 13, outline: "none", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }} />
          <button onClick={() => input.trim() && runAdvisor(input)} disabled={loading || !input.trim()} style={{ background: loading ? "#3A3E4A" : "linear-gradient(135deg, #00E5A0, #7C8CF8)", border: "none", borderRadius: 8, padding: "12px 16px", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, color: "#0B0D11", fontWeight: 600, fontSize: 13 }}>
            {loading ? <span style={{ color: "#6B7080" }}>Thinking...</span> : <><Icons.Send /> Ask</>}
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {messages.length === 0 && !loading && (
            <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 32, textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12, color: "#3A3E4A" }}><Icons.AI /></div>
              <div style={{ fontSize: 15, color: "#E8E9EC", fontWeight: 500, marginBottom: 8 }}>Your AI Advisor is ready</div>
              <div style={{ fontSize: 13, color: "#6B7080", maxWidth: 400, margin: "0 auto", lineHeight: 1.6 }}>
                Pick a quick analysis above or ask a custom question. The advisor sees all your dashboard data and gives you strategic next steps.
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} style={{ background: m.role === "assistant" ? "#13151A" : "rgba(0,229,160,0.05)", border: `1px solid ${m.role === "assistant" ? "#1E2028" : "rgba(0,229,160,0.15)"}`, borderRadius: 12, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ background: m.role === "assistant" ? "linear-gradient(135deg, #00E5A0, #7C8CF8)" : "rgba(0,229,160,0.15)", borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, color: m.role === "assistant" ? "#0B0D11" : "#00E5A0" }}>{m.role === "assistant" ? "ADVISOR" : "YOU"}</span>
                <span style={{ fontSize: 10, color: "#3A3E4A" }}>{m.timestamp}</span>
              </div>
              <div style={{ fontSize: 13, color: "#E8E9EC", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{m.content}</div>
            </div>
          ))}

          {loading && (
            <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ display: "flex", gap: 4 }}>
                {[0, 1, 2].map(i => (<div key={i} style={{ width: 8, height: 8, borderRadius: 4, background: "#00E5A0", animation: `bopulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />))}
              </div>
              <span style={{ fontSize: 13, color: "#6B7080" }}>Analyzing your data...</span>
            </div>
          )}
        </div>
        <style>{`@keyframes bopulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }`}</style>
      </div>
    );
  };

  // ============================================================
  // DASHBOARD
  // ============================================================
  const DashboardView = () => (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Command Center</h2>
        <p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>BlessedOps — Full operational overview</p>
      </div>

      <div onClick={() => setActiveTab("advisor")} style={{ background: "linear-gradient(135deg, rgba(0,229,160,0.08), rgba(124,140,248,0.08))", border: "1px solid rgba(0,229,160,0.2)", borderRadius: 12, padding: "14px 20px", marginBottom: 20, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#00E5A0" }}><Icons.Zap /></span>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#E8E9EC" }}>AI Advisor ready — get your daily operational brief</span>
        </div>
        <span style={{ color: "#6B7080" }}><Icons.Arrow /></span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
        <StatCard label="Monthly Income" value={`$${totalMonthlyIncome.toLocaleString()}`} sub={`$${passiveIncome} passive`} />
        <StatCard label="Credit Score" value={creditScore || "—"} sub={`Goal: ${creditGoal}`} color={creditScore >= 750 ? "#00E5A0" : creditScore >= 700 ? "#FFD166" : "#FF6B6B"} />
        <StatCard label="Active Tasks" value={activeTasks} sub={`${pendingTasks} pending · ${completedTasks} done`} color="#7C8CF8" />
        <StatCard label="Credit Plays" value={`${strategiesDone}/${credit.strategies.length}`} sub="Strategies completed" color="#E8A0FF" />
      </div>

      {(() => {
        const lastEntry = journal.entries[0];
        const daysSinceJournal = lastEntry ? Math.floor((Date.now() - new Date(lastEntry.date).getTime()) / 86400000) : null;
        const rechargeIdeas = [
          "Hot springs or sauna — reset the nervous system",
          "Nature walk with no destination, no phone",
          "Digital detox: phone off by 6pm for one day",
          "Prayer and fasting retreat — half day minimum",
          "Gym session — move the body, clear the head",
          "Journaling at a coffee shop, no laptop",
          "Long drive with worship music, no agenda",
          "Cook a real meal from scratch — no fast food",
        ];
        let nudge, nudgeColor, nudgeBorder;
        if (activeTasks >= 4 && (daysSinceJournal === null || daysSinceJournal >= 3)) {
          nudge = `${activeTasks} active tasks and ${daysSinceJournal === null ? "no journal entries yet" : `no journal entry in ${daysSinceJournal} days`} — you've been grinding. Consider a reset day.`;
          nudgeColor = "#FF8C66"; nudgeBorder = "rgba(255,140,102,0.25)";
        } else if (activeTasks >= 3 && daysSinceJournal === null) {
          nudge = `${activeTasks} active tasks, no journal entries yet — take 20 minutes to breathe and check in with yourself.`;
          nudgeColor = "#FFD166"; nudgeBorder = "rgba(255,209,102,0.25)";
        } else if (daysSinceJournal !== null && daysSinceJournal >= 7) {
          nudge = `It's been ${daysSinceJournal} days since your last journal entry. A check-in with yourself is overdue.`;
          nudgeColor = "#FFD166"; nudgeBorder = "rgba(255,209,102,0.25)";
        } else {
          nudge = "You're keeping a solid rhythm. Stay consistent — recharge before you need to, not after.";
          nudgeColor = "#00E5A0"; nudgeBorder = "rgba(0,229,160,0.2)";
        }
        const ideas = rechargeIdeas.sort(() => Math.random() - 0.5).slice(0, 4);
        return (
          <div style={{ background: "#13151A", border: `1px solid ${nudgeBorder}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: nudgeColor, marginBottom: 10 }}>Recharge & Retreat</div>
            <div style={{ fontSize: 13, color: "#E8E9EC", lineHeight: 1.6, marginBottom: 14 }}>{nudge}</div>
            <div style={{ fontSize: 11, color: "#6B7080", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Ideas</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {ideas.map((idea, i) => (
                <div key={i} style={{ fontSize: 12, color: "#8A8F98", display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ color: nudgeColor, marginTop: 1, flexShrink: 0 }}>—</span>{idea}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      <div className="bo-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: 20 }}>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#00E5A0", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><Icons.Money /> Income Streams</div>
          {finances.incomeStreams.map(s => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #1A1D23" }}>
              <div><div style={{ fontSize: 13, color: "#E8E9EC" }}>{s.name}</div><div style={{ fontSize: 11, color: "#6B7080" }}>{s.type}</div></div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#00E5A0", fontFamily: "'JetBrains Mono', monospace" }}>${Number(s.monthly || 0).toLocaleString()}/mo</div>
            </div>
          ))}
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#E8A0FF", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><Icons.Credit /> Credit to 750</div>
          <ProgressBar value={creditScore} max={creditGoal} color="#E8A0FF" height={10} label={`${creditScore} → ${creditGoal}`} />
          <div style={{ marginTop: 16 }}>
            {credit.strategies.slice(0, 4).map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 12, color: s.done ? "#6B7080" : "#E8E9EC", textDecoration: s.done ? "line-through" : "none" }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${s.done ? "#00E5A0" : "#3A3E4A"}`, display: "flex", alignItems: "center", justifyContent: "center", background: s.done ? "rgba(0,229,160,0.15)" : "transparent" }}>{s.done && <Icons.Check />}</div>
                {s.text}
              </div>
            ))}
          </div>
          <button onClick={() => setActiveTab("credit")} style={{ marginTop: 12, background: "none", border: "none", color: "#E8A0FF", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, padding: 0 }}>View all strategies <Icons.Arrow /></button>
        </div>
      </div>

      <div className="bo-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#7C8CF8", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><Icons.Tasks /> Active Tasks</div>
          {tasks.items.filter(t => t.status === "active").slice(0, 4).map(t => (
            <div key={t.id} style={{ fontSize: 12, color: "#E8E9EC", padding: "6px 0", borderBottom: "1px solid #1A1D23", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: t.priority === "high" ? "#FF6B6B" : t.priority === "medium" ? "#FFD166" : "#6B7080" }} />{t.text}
            </div>
          ))}
          {activeTasks === 0 && <div style={{ fontSize: 12, color: "#6B7080" }}>No active tasks</div>}
          <button onClick={() => setActiveTab("tasks")} style={{ marginTop: 12, background: "none", border: "none", color: "#7C8CF8", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, padding: 0 }}>Manage tasks <Icons.Arrow /></button>
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#FFD166", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><Icons.Learn /> Certifications</div>
          {learning.certifications.map(c => (
            <div key={c.id} style={{ padding: "8px 0", borderBottom: "1px solid #1A1D23" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#E8E9EC" }}>{c.name}</span>
                <span style={{ color: c.status === "completed" ? "#00E5A0" : c.status === "in-progress" ? "#FFD166" : "#6B7080", fontSize: 11, textTransform: "uppercase" }}>{c.status}</span>
              </div>
              {c.status === "in-progress" && <ProgressBar value={c.progress || 0} color="#FFD166" height={4} />}
            </div>
          ))}
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#FF8C66", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><Icons.Content /> Content Series</div>
          {content.series.map(s => (
            <div key={s.id} style={{ padding: "8px 0", borderBottom: "1px solid #1A1D23", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><div style={{ fontSize: 12, color: "#E8E9EC" }}>{s.name}</div><div style={{ fontSize: 11, color: "#6B7080" }}>{s.platform} · {s.frequency}</div></div>
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: s.active ? "rgba(0,229,160,0.15)" : "rgba(255,107,107,0.15)", color: s.active ? "#00E5A0" : "#FF6B6B" }}>{s.active ? "ACTIVE" : "PAUSED"}</span>
            </div>
          ))}
          <button onClick={() => setActiveTab("content")} style={{ marginTop: 12, background: "none", border: "none", color: "#FF8C66", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, padding: 0 }}>Content hub <Icons.Arrow /></button>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // MONEY & REVENUE
  // ============================================================
  const MoneyView = () => {
    const [editingStream, setEditingStream] = useState(null);
    const [newAmount, setNewAmount] = useState("");
    const updateStream = (id, monthly) => { setFinances({ ...finances, incomeStreams: finances.incomeStreams.map(s => s.id === id ? { ...s, monthly: Number(monthly) || 0 } : s) }); setEditingStream(null); };
    const addStream = () => { const id = Date.now(); setFinances({ ...finances, incomeStreams: [...finances.incomeStreams, { id, name: "New Stream", type: "active", monthly: 0, note: "" }] }); setEditingStream(id); };
    const removeStream = (id) => { setFinances({ ...finances, incomeStreams: finances.incomeStreams.filter(s => s.id !== id) }); };
    const updateStreamField = (id, field, value) => { setFinances({ ...finances, incomeStreams: finances.incomeStreams.map(s => s.id === id ? { ...s, [field]: value } : s) }); };

    return (
      <div>
        <div style={{ marginBottom: 28 }}><h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Money & Revenue</h2><p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Track every dollar coming in and going out</p></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
          <StatCard label="Total Monthly" value={`$${totalMonthlyIncome.toLocaleString()}`} color="#00E5A0" />
          <StatCard label="Passive Income" value={`$${passiveIncome.toLocaleString()}`} sub={totalMonthlyIncome > 0 ? `${Math.round((passiveIncome / totalMonthlyIncome) * 100)}% of total` : "0%"} color="#7C8CF8" />
          <StatCard label="Active Income" value={`$${(totalMonthlyIncome - passiveIncome).toLocaleString()}`} color="#FFD166" />
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
          <SectionHeader title="Income Streams" onAdd={addStream} addLabel="Add Stream" />
          {finances.incomeStreams.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: "1px solid #1A1D23" }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: s.type === "active" ? "#FFD166" : s.type === "semi-passive" ? "#7C8CF8" : "#00E5A0" }} />
              <div style={{ flex: 1 }}>
                <DeferredInput value={s.name} onCommit={v => updateStreamField(s.id, "name", v)} style={{ background: "none", border: "none", color: "#E8E9EC", fontSize: 14, fontWeight: 500, width: "100%", outline: "none" }} />
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  {["active", "semi-passive", "passive"].map(t => (<button key={t} onClick={() => updateStreamField(s.id, "type", t)} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, border: "1px solid", borderColor: s.type === t ? "#00E5A0" : "#3A3E4A", background: s.type === t ? "rgba(0,229,160,0.15)" : "transparent", color: s.type === t ? "#00E5A0" : "#6B7080", cursor: "pointer" }}>{t}</button>))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "#6B7080", fontSize: 14 }}>$</span>
                {editingStream === s.id ? (
                  <input value={newAmount} onChange={e => setNewAmount(e.target.value)} onBlur={() => updateStream(s.id, newAmount)} onKeyDown={e => e.key === "Enter" && updateStream(s.id, newAmount)} inputMode="numeric" style={{ width: 80, background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 6, color: "#00E5A0", fontSize: 18, fontWeight: 700, padding: "4px 8px", fontFamily: "'JetBrains Mono', monospace", outline: "none" }} />
                ) : (
                  <span onClick={() => { setEditingStream(s.id); setNewAmount(s.monthly || ""); }} style={{ fontSize: 18, fontWeight: 700, color: "#00E5A0", fontFamily: "'JetBrains Mono', monospace", cursor: "pointer", minWidth: 60 }}>{Number(s.monthly || 0).toLocaleString()}</span>
                )}
                <span style={{ color: "#6B7080", fontSize: 12 }}>/mo</span>
              </div>
              <button onClick={() => removeStream(s.id)} style={{ background: "none", border: "none", color: "#6B7080", cursor: "pointer", padding: 4 }}><Icons.Trash /></button>
            </div>
          ))}
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24, marginTop: 16 }}>
          <SectionHeader title="Income Tier Roadmap" />
          {[
            { tier: "Tier 1", label: "Near-Term", items: "Digital templates, mini guides, audit reports, affiliate tools", color: "#00E5A0", target: "$500/mo" },
            { tier: "Tier 2", label: "Scalable", items: "Subscriptions, security dashboards, automated audits", color: "#7C8CF8", target: "$2,000/mo" },
            { tier: "Tier 3", label: "Compounding", items: "Courses, toolkits, communities, licensing, speaking", color: "#E8A0FF", target: "$5,000+/mo" },
          ].map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "14px 0", borderBottom: "1px solid #1A1D23" }}>
              <div style={{ background: `${t.color}20`, border: `1px solid ${t.color}40`, borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: t.color, whiteSpace: "nowrap" }}>{t.tier}</div>
              <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500, color: "#E8E9EC" }}>{t.label}</div><div style={{ fontSize: 12, color: "#6B7080", marginTop: 2 }}>{t.items}</div></div>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.color, fontFamily: "'JetBrains Mono', monospace" }}>{t.target}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============================================================
  // CREDIT
  // ============================================================
  const CreditView = () => {
    const [scoreInput, setScoreInput] = useState("");
    const [showAddAccount, setShowAddAccount] = useState(false);
    const [newAccount, setNewAccount] = useState({ name: "", type: "credit_card", limit: "", balance: "", apr: "" });
    const updateScore = () => { const val = Number(scoreInput); if (val > 0) { setCredit({ ...credit, currentScore: val, history: [...credit.history, { score: val, date: new Date().toISOString().split("T")[0] }] }); setScoreInput(""); } };
    const toggleStrategy = (id) => { setCredit({ ...credit, strategies: credit.strategies.map(s => s.id === id ? { ...s, done: !s.done } : s) }); };
    const addStrategy = () => { setCredit({ ...credit, strategies: [...credit.strategies, { id: Date.now(), text: "New strategy...", done: false }] }); };
    const updateStrategyText = (id, text) => { setCredit({ ...credit, strategies: credit.strategies.map(s => s.id === id ? { ...s, text } : s) }); };
    const removeStrategy = (id) => { setCredit({ ...credit, strategies: credit.strategies.filter(s => s.id !== id) }); };
    const addAccount = () => { setCredit({ ...credit, accounts: [...credit.accounts, { ...newAccount, id: Date.now(), limit: Number(newAccount.limit) || 0, balance: Number(newAccount.balance) || 0, apr: Number(newAccount.apr) || 0 }] }); setNewAccount({ name: "", type: "credit_card", limit: "", balance: "", apr: "" }); setShowAddAccount(false); };
    const removeAccount = (id) => { setCredit({ ...credit, accounts: credit.accounts.filter(a => a.id !== id) }); };
    const updateAccountField = (id, field, value) => { setCredit({ ...credit, accounts: credit.accounts.map(a => a.id === id ? { ...a, [field]: field === "name" || field === "type" ? value : Number(value) || 0 } : a) }); };
    const totalLimit = credit.accounts.reduce((s, a) => s + (Number(a.limit) || 0), 0);
    const totalBalance = credit.accounts.reduce((s, a) => s + (Number(a.balance) || 0), 0);
    const utilization = totalLimit > 0 ? ((totalBalance / totalLimit) * 100).toFixed(1) : 0;

    return (
      <div>
        <div style={{ marginBottom: 28 }}><h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Credit Building</h2><p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Strategic path to 750+ and premium access</p></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
          <StatCard label="Current Score" value={creditScore || "—"} color={creditScore >= 750 ? "#00E5A0" : creditScore >= 700 ? "#FFD166" : creditScore > 0 ? "#FF6B6B" : "#6B7080"} />
          <StatCard label="Goal" value={creditGoal} color="#E8A0FF" />
          <StatCard label="Utilization" value={`${utilization}%`} sub={utilization <= 10 ? "Excellent" : utilization <= 30 ? "Good" : "Too high"} color={utilization <= 10 ? "#00E5A0" : utilization <= 30 ? "#FFD166" : "#FF6B6B"} />
          <StatCard label="Total Credit" value={`$${totalLimit.toLocaleString()}`} sub={`$${totalBalance.toLocaleString()} used`} color="#7C8CF8" />
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20, marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#6B7080" }}>Log score:</span>
          <input value={scoreInput} onChange={e => setScoreInput(e.target.value)} onKeyDown={e => e.key === "Enter" && updateScore()} placeholder="e.g. 685" inputMode="numeric" style={{ width: 100, background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 8, color: "#E8E9EC", fontSize: 16, padding: "6px 12px", fontFamily: "'JetBrains Mono', monospace", outline: "none" }} />
          <button onClick={updateScore} style={{ background: "#E8A0FF", color: "#0B0D11", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Update</button>
          {credit.history.length > 0 && <span style={{ fontSize: 11, color: "#6B7080" }}>Last: {credit.history[credit.history.length - 1].date}</span>}
        </div>
        {credit.history.length > 1 && (
          <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <SectionHeader title="Score History" />
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80 }}>
              {credit.history.slice(-20).map((h, i) => {
                const scores = credit.history.slice(-20).map(x => x.score);
                const min = Math.min(...scores) - 20, max = Math.max(...scores) + 20;
                const pct = ((h.score - min) / (max - min)) * 100;
                return (<div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}><span style={{ fontSize: 9, color: "#6B7080" }}>{h.score}</span><div style={{ width: "100%", height: `${pct}%`, background: h.score >= 750 ? "#00E5A0" : h.score >= 700 ? "#FFD166" : "#E8A0FF", borderRadius: 3, minHeight: 4 }} /></div>);
              })}
            </div>
          </div>
        )}
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24, marginBottom: 16 }}>
          <SectionHeader title="Credit Accounts" onAdd={() => setShowAddAccount(!showAddAccount)} addLabel="Add Account" />
          {showAddAccount && (
            <div style={{ background: "#1A1D23", borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <input value={newAccount.name} onChange={e => setNewAccount({ ...newAccount, name: e.target.value })} placeholder="Card name" style={{ flex: 1, minWidth: 120, background: "#0B0D11", border: "1px solid #3A3E4A", borderRadius: 6, color: "#E8E9EC", padding: "6px 10px", fontSize: 12, outline: "none" }} />
              <select value={newAccount.type} onChange={e => setNewAccount({ ...newAccount, type: e.target.value })} style={{ background: "#0B0D11", border: "1px solid #3A3E4A", borderRadius: 6, color: "#E8E9EC", padding: "6px 10px", fontSize: 12, outline: "none" }}><option value="credit_card">Credit Card</option><option value="loc">Line of Credit</option><option value="installment">Installment Loan</option></select>
              <input value={newAccount.limit} onChange={e => setNewAccount({ ...newAccount, limit: e.target.value })} placeholder="Limit" style={{ width: 80, background: "#0B0D11", border: "1px solid #3A3E4A", borderRadius: 6, color: "#E8E9EC", padding: "6px 10px", fontSize: 12, outline: "none" }} />
              <input value={newAccount.balance} onChange={e => setNewAccount({ ...newAccount, balance: e.target.value })} placeholder="Balance" style={{ width: 80, background: "#0B0D11", border: "1px solid #3A3E4A", borderRadius: 6, color: "#E8E9EC", padding: "6px 10px", fontSize: 12, outline: "none" }} />
              <input value={newAccount.apr} onChange={e => setNewAccount({ ...newAccount, apr: e.target.value })} placeholder="APR %" style={{ width: 70, background: "#0B0D11", border: "1px solid #3A3E4A", borderRadius: 6, color: "#E8E9EC", padding: "6px 10px", fontSize: 12, outline: "none" }} />
              <button onClick={addAccount} style={{ background: "#00E5A0", color: "#0B0D11", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Save</button>
            </div>
          )}
          {credit.accounts.length === 0 && <div style={{ fontSize: 13, color: "#6B7080", padding: "12px 0" }}>No accounts added yet</div>}
          {credit.accounts.map(a => {
            const acctUtil = a.limit > 0 ? ((a.balance / a.limit) * 100).toFixed(1) : 0;
            return (
              <div key={a.id} style={{ padding: "14px 0", borderBottom: "1px solid #1A1D23" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <DeferredInput value={a.name} onCommit={v => updateAccountField(a.id, "name", v)} style={{ background: "none", border: "none", color: "#E8E9EC", fontSize: 14, fontWeight: 500, outline: "none" }} />
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "rgba(124,140,248,0.15)", color: "#7C8CF8" }}>{a.type === "credit_card" ? "CC" : a.type === "loc" ? "LOC" : "LOAN"}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}><span style={{ fontSize: 12, color: "#6B7080" }}>{a.apr}% APR</span><button onClick={() => removeAccount(a.id)} style={{ background: "none", border: "none", color: "#6B7080", cursor: "pointer", padding: 2 }}><Icons.Trash /></button></div>
                </div>
                <ProgressBar value={a.balance} max={a.limit} color={acctUtil <= 10 ? "#00E5A0" : acctUtil <= 30 ? "#FFD166" : "#FF6B6B"} height={6} label={`$${a.balance.toLocaleString()} / $${a.limit.toLocaleString()} (${acctUtil}% util)`} />
              </div>
            );
          })}
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
          <SectionHeader title="750 Strategy Checklist" onAdd={addStrategy} addLabel="Add Play" />
          {credit.strategies.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #1A1D23" }}>
              <button onClick={() => toggleStrategy(s.id)} style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${s.done ? "#00E5A0" : "#3A3E4A"}`, display: "flex", alignItems: "center", justifyContent: "center", background: s.done ? "rgba(0,229,160,0.15)" : "transparent", cursor: "pointer", flexShrink: 0 }}>{s.done && <Icons.Check />}</button>
              <DeferredInput value={s.text} onCommit={v => updateStrategyText(s.id, v)} style={{ flex: 1, background: "none", border: "none", color: s.done ? "#6B7080" : "#E8E9EC", fontSize: 13, outline: "none", textDecoration: s.done ? "line-through" : "none" }} />
              <button onClick={() => removeStrategy(s.id)} style={{ background: "none", border: "none", color: "#3A3E4A", cursor: "pointer", padding: 2 }}><Icons.Trash /></button>
            </div>
          ))}
          <div style={{ marginTop: 12 }}><ProgressBar value={strategiesDone} max={credit.strategies.length} color="#E8A0FF" height={6} label={`${strategiesDone} of ${credit.strategies.length} complete`} /></div>
        </div>
      </div>
    );
  };

  // ============================================================
  // TASKS
  // ============================================================
  const TasksView = () => {
    const addTask = () => { setTasks({ ...tasks, items: [...tasks.items, { id: Date.now(), text: "New task...", status: "pending", priority: "medium", category: "general" }] }); };
    const updateTask = (id, field, value) => { setTasks({ ...tasks, items: tasks.items.map(t => t.id === id ? { ...t, [field]: value } : t) }); };
    const removeTask = (id) => { setTasks({ ...tasks, items: tasks.items.filter(t => t.id !== id) }); };
    const sc = { active: "#00E5A0", pending: "#FFD166", done: "#6B7080" };
    const pc = { high: "#FF6B6B", medium: "#FFD166", low: "#6B7080" };
    return (
      <div>
        <div style={{ marginBottom: 28 }}><h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Tasks & Projects</h2><p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Everything pending, active, and done</p></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}><StatCard label="Active" value={activeTasks} color="#00E5A0" /><StatCard label="Pending" value={pendingTasks} color="#FFD166" /><StatCard label="Completed" value={completedTasks} color="#6B7080" /></div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
          <SectionHeader title="All Tasks" onAdd={addTask} addLabel="Add Task" />
          {["active", "pending", "done"].map(status => {
            const f = tasks.items.filter(t => t.status === status);
            if (!f.length) return null;
            return (<div key={status} style={{ marginBottom: 20 }}><div style={{ fontSize: 11, fontWeight: 600, color: sc[status], textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{status} ({f.length})</div>
              {f.map(t => (<div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #1A1D23" }}><span style={{ width: 8, height: 8, borderRadius: 4, background: pc[t.priority], flexShrink: 0 }} /><DeferredInput value={t.text} onCommit={v => updateTask(t.id, "text", v)} style={{ flex: 1, background: "none", border: "none", color: t.status === "done" ? "#6B7080" : "#E8E9EC", fontSize: 13, outline: "none", textDecoration: t.status === "done" ? "line-through" : "none" }} /><select value={t.priority} onChange={e => updateTask(t.id, "priority", e.target.value)} style={{ background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 6, color: pc[t.priority], fontSize: 10, padding: "3px 6px", outline: "none" }}><option value="high">HIGH</option><option value="medium">MED</option><option value="low">LOW</option></select><select value={t.status} onChange={e => updateTask(t.id, "status", e.target.value)} style={{ background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 6, color: sc[t.status], fontSize: 10, padding: "3px 6px", outline: "none" }}><option value="active">ACTIVE</option><option value="pending">PENDING</option><option value="done">DONE</option></select><button onClick={() => removeTask(t.id)} style={{ background: "none", border: "none", color: "#3A3E4A", cursor: "pointer" }}><Icons.Trash /></button></div>))}
            </div>);
          })}
        </div>
      </div>
    );
  };

  // ============================================================
  // LEARNING
  // ============================================================
  const LearningView = () => {
    const updateCert = (id, field, value) => { setLearning({ ...learning, certifications: learning.certifications.map(c => c.id === id ? { ...c, [field]: value } : c) }); };
    const updateSkill = (id, level) => { setLearning({ ...learning, skills: learning.skills.map(s => s.id === id ? { ...s, level: Number(level) } : s) }); };
    const addCert = () => { setLearning({ ...learning, certifications: [...learning.certifications, { id: Date.now(), name: "New Cert", status: "planned", progress: 0, targetDate: "" }] }); };
    const addSkill = () => { setLearning({ ...learning, skills: [...learning.skills, { id: Date.now(), name: "New Skill", level: 10 }] }); };
    return (
      <div>
        <div style={{ marginBottom: 28 }}><h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Learning & Certifications</h2><p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Skill stack and cert progress</p></div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24, marginBottom: 16 }}>
          <SectionHeader title="Certifications" onAdd={addCert} addLabel="Add Cert" />
          {learning.certifications.map(c => (
            <div key={c.id} style={{ padding: "14px 0", borderBottom: "1px solid #1A1D23" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <DeferredInput value={c.name} onCommit={v => updateCert(c.id, "name", v)} style={{ background: "none", border: "none", color: "#E8E9EC", fontSize: 14, fontWeight: 500, outline: "none" }} />
                <select value={c.status} onChange={e => updateCert(c.id, "status", e.target.value)} style={{ background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 6, color: c.status === "completed" ? "#00E5A0" : c.status === "in-progress" ? "#FFD166" : "#6B7080", fontSize: 11, padding: "3px 8px", outline: "none" }}><option value="completed">COMPLETED</option><option value="in-progress">IN PROGRESS</option><option value="planned">PLANNED</option></select>
              </div>
              {c.status === "in-progress" && (<div style={{ display: "flex", alignItems: "center", gap: 12 }}><div style={{ flex: 1 }}><ProgressBar value={c.progress || 0} color="#FFD166" height={6} /></div><input type="range" min={0} max={100} value={c.progress || 0} onChange={e => updateCert(c.id, "progress", Number(e.target.value))} style={{ width: 80, accentColor: "#FFD166" }} /><span style={{ fontSize: 12, color: "#FFD166", fontFamily: "'JetBrains Mono', monospace", minWidth: 35 }}>{c.progress || 0}%</span></div>)}
            </div>
          ))}
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
          <SectionHeader title="Skill Levels" onAdd={addSkill} addLabel="Add Skill" />
          {learning.skills.map(s => (
            <div key={s.id} style={{ padding: "10px 0", borderBottom: "1px solid #1A1D23" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><DeferredInput value={s.name} onCommit={v => setLearning({ ...learning, skills: learning.skills.map(sk => sk.id === s.id ? { ...sk, name: v } : sk) })} style={{ background: "none", border: "none", color: "#E8E9EC", fontSize: 13, outline: "none" }} /><span style={{ fontSize: 12, color: "#7C8CF8", fontFamily: "'JetBrains Mono', monospace" }}>{s.level}%</span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ flex: 1 }}><ProgressBar value={s.level} color="#7C8CF8" height={5} /></div><input type="range" min={0} max={100} value={s.level} onChange={e => updateSkill(s.id, e.target.value)} style={{ width: 80, accentColor: "#7C8CF8" }} /></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============================================================
  // CONTENT
  // ============================================================
  const ContentView = () => {
    const addIdea = () => { setContent({ ...content, ideas: [...content.ideas, { id: Date.now(), text: "", status: "idea" }] }); };
    const toggleSeries = (id) => { setContent({ ...content, series: content.series.map(s => s.id === id ? { ...s, active: !s.active } : s) }); };
    const updateIdea = (id, field, value) => { setContent({ ...content, ideas: content.ideas.map(i => i.id === id ? { ...i, [field]: value } : i) }); };
    const removeIdea = (id) => { setContent({ ...content, ideas: content.ideas.filter(i => i.id !== id) }); };
    const sc = { idea: "#6B7080", scripted: "#FFD166", recorded: "#7C8CF8", published: "#00E5A0" };
    return (
      <div>
        <div style={{ marginBottom: 28 }}><h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Content Pipeline</h2><p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Ideas → Scripts → Published</p></div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24, marginBottom: 16 }}>
          <SectionHeader title="Content Series" />
          {content.series.map(s => (<div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #1A1D23" }}><div><div style={{ fontSize: 14, fontWeight: 500, color: "#E8E9EC" }}>{s.name}</div><div style={{ fontSize: 12, color: "#6B7080" }}>{s.platform} · {s.frequency}</div></div><button onClick={() => toggleSeries(s.id)} style={{ fontSize: 11, padding: "4px 12px", borderRadius: 10, border: "1px solid", borderColor: s.active ? "#00E5A0" : "#3A3E4A", background: s.active ? "rgba(0,229,160,0.15)" : "transparent", color: s.active ? "#00E5A0" : "#6B7080", cursor: "pointer" }}>{s.active ? "ACTIVE" : "PAUSED"}</button></div>))}
        </div>
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
          <SectionHeader title="Content Ideas & Pipeline" onAdd={addIdea} addLabel="Add Idea" />
          {content.ideas.length === 0 && <div style={{ fontSize: 13, color: "#6B7080", padding: "12px 0" }}>No content in pipeline yet</div>}
          {content.ideas.map(i => (<div key={i.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #1A1D23" }}><DeferredInput value={i.text} onCommit={v => updateIdea(i.id, "text", v)} placeholder="Content idea..." style={{ flex: 1, background: "none", border: "none", color: "#E8E9EC", fontSize: 13, outline: "none" }} /><select value={i.status} onChange={e => updateIdea(i.id, "status", e.target.value)} style={{ background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 6, color: sc[i.status], fontSize: 10, padding: "3px 8px", outline: "none" }}><option value="idea">IDEA</option><option value="scripted">SCRIPTED</option><option value="recorded">RECORDED</option><option value="published">PUBLISHED</option></select><button onClick={() => removeIdea(i.id)} style={{ background: "none", border: "none", color: "#3A3E4A", cursor: "pointer" }}><Icons.Trash /></button></div>))}
        </div>
      </div>
    );
  };

  // ============================================================
  // PLAN (Day Planner + Goals + Burnout Prevention)
  // ============================================================
  const PlanView = () => {
    const [planLoading, setPlanLoading] = useState(false);
    const [whatChanged, setWhatChanged] = useState("");
    const todayStr = new Date().toISOString().split("T")[0];
    const STALE_DOMAIN_DAYS = 7;

    const updateGoal = (level, text) => {
      setPlan({ ...plan, goals: { ...plan.goals, [level]: { text, updated: new Date().toISOString() } } });
    };

    const moveGoal = (level, newText, reason) => {
      const prev = plan.goals[level]?.text || "";
      setPlan({
        ...plan,
        goals: { ...plan.goals, [level]: { text: newText, updated: new Date().toISOString() } },
        goalMoves: [{ level, from: prev, to: newText, reason: reason || "no reason given", date: todayStr }, ...(plan.goalMoves || [])].slice(0, 50),
      });
    };

    const touchDomain = (key) => {
      setPlan({
        ...plan,
        domains: { ...plan.domains, [key]: { ...plan.domains[key], lastTouched: todayStr } },
      });
      setActivityFeed([{ id: Date.now(), date: todayStr, source: "plan", text: `Touched life domain: ${plan.domains[key]?.label || key}` }, ...activityFeed]);
    };

    const daysSince = (iso) => {
      if (!iso) return null;
      return Math.floor((new Date(todayStr).getTime() - new Date(iso).getTime()) / 86400000);
    };

    const generatePlan = async (incrementalUpdate) => {
      setPlanLoading(true);
      const goalsBlock = Object.entries(plan.goals).map(([k, v]) => `${k.toUpperCase()}: ${v?.text || "(not set)"}`).join("\n");
      const domainsBlock = Object.entries(plan.domains).map(([k, v]) => {
        const days = daysSince(v.lastTouched);
        return `${v.emoji} ${v.label}: ${v.lastTouched ? `${days}d ago` : "NEVER touched"}${days != null && days >= STALE_DOMAIN_DAYS ? " ⚠ STALE" : ""}`;
      }).join("\n");
      const activeTasks = (tasks.items || []).filter(t => t.status === "active").slice(0, 8).map(t => `- ${t.text}`).join("\n");
      const recentLogs = (quickLogs || []).filter(l => l.date === todayStr).slice(0, 5).map(l => `- ${l.text}`).join("\n");

      const systemPrompt = `You are the BlessedOps Day Planner. You serve Kingsley Olukanni (operator + founder). Today is ${todayStr}. He is running a 90-day bug-bounty + Securva launch challenge (Apr 10 - Jul 9, 2026).

Your job: generate a realistic time-blocked plan for the rest of TODAY based on his goals + active tasks + life domains + commitments.

Rules:
- Time blocks in 60-90 minute chunks. Include specific start times.
- Always include AT LEAST one stale-life-domain block (faith / family / sleep / etc) if any domain is 7+ days untouched. Burnout prevention is HARD RULE.
- Don't overpack. Realistic energy budget: 6-8 productive hours per day max.
- Include buffer time and meal breaks.
- Adjust to current time-of-day - don't suggest 6am if it's already 10am.
- If the operator just told you what changed (incremental update), respect existing completed/pending blocks.
- No em-dashes. Use hyphens or rephrase.

Output ONLY a numbered list of time blocks. Each line: "HH:MM - HH:MM | Block label". One per line. End with one short paragraph rationale (under 60 words).`;

      const now = new Date();
      const userPrompt = `Current time: ${now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}

GOALS HIERARCHY:
${goalsBlock}

LIFE DOMAINS (staleness):
${domainsBlock}

ACTIVE TASKS (top 8):
${activeTasks || "(none)"}

TODAY'S QUICK LOGS:
${recentLogs || "(none yet)"}

${plan.todayPlan?.date === todayStr && plan.todayPlan?.blocks?.length ? `EXISTING PLAN:\n${plan.todayPlan.blocks.map((b, i) => `${i + 1}. ${b.time} | ${b.label} [${b.status}]`).join("\n")}\n` : ""}

${incrementalUpdate ? `WHAT JUST CHANGED:\n${incrementalUpdate}\n\nReplan the REMAINING time of today. Keep blocks already marked done.` : "Generate a fresh plan for the rest of today."}`;

      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const response = await fetch("/api/claude", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${s?.access_token}` },
          body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 800, system: systemPrompt, messages: [{ role: "user", content: userPrompt }] }),
        });
        const result = await response.json();
        const text = result.content?.map(c => c.text || "").join("\n") || "Could not generate plan.";

        const blocks = [];
        const lines = text.split("\n");
        let rationale = "";
        for (const line of lines) {
          const m = line.match(/^\s*\d+\.\s*(\d{1,2}:\d{2}\s*[apAP]?[mM]?\s*-\s*\d{1,2}:\d{2}\s*[apAP]?[mM]?)\s*[|\-:]\s*(.+)$/) || line.match(/^\s*(\d{1,2}:\d{2}\s*[apAP]?[mM]?\s*-\s*\d{1,2}:\d{2}\s*[apAP]?[mM]?)\s*[|\-:]\s*(.+)$/);
          if (m) blocks.push({ id: Date.now() + Math.random(), time: m[1].trim(), label: m[2].trim(), status: "pending" });
          else if (line.trim() && !line.match(/^\s*\d+\./)) rationale += line + "\n";
        }

        setPlan({
          ...plan,
          todayPlan: {
            date: todayStr,
            blocks: incrementalUpdate
              ? [...(plan.todayPlan?.blocks?.filter(b => b.status === "done") || []), ...blocks]
              : blocks,
            notes: rationale.trim(),
            generatedAt: new Date().toISOString(),
          },
        });
        setWhatChanged("");
      } catch (e) {
        console.warn("Plan generation failed:", e);
      }
      setPlanLoading(false);
    };

    const updateBlockStatus = (id, status) => {
      setPlan({
        ...plan,
        todayPlan: {
          ...plan.todayPlan,
          blocks: plan.todayPlan.blocks.map(b => b.id === id ? { ...b, status } : b),
        },
      });
    };

    const goalLevels = [
      { id: "year",    label: "YEAR",    color: "#FFC857" },
      { id: "quarter", label: "QUARTER (90 days)", color: "#FF8C66" },
      { id: "month",   label: "MONTH",   color: "#E8A0FF" },
      { id: "week",    label: "WEEK",    color: "#7C8CF8" },
      { id: "today",   label: "TODAY 1-3", color: "#00E5A0" },
    ];

    const staleDomains = Object.entries(plan.domains).filter(([_, v]) => {
      const d = daysSince(v.lastTouched);
      return d == null || d >= STALE_DOMAIN_DAYS;
    });

    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Day Plan + Goals</h2>
          <p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Goals hierarchy + AI-generated day plan + burnout prevention</p>
        </div>

        {/* GOALS HIERARCHY */}
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 14, fontWeight: 700, letterSpacing: 0.8 }}>GOALS HIERARCHY</div>
          {goalLevels.map(({ id: lvl, label, color }) => (
            <div key={lvl} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 10, color, fontWeight: 700, letterSpacing: 0.5, fontFamily: "'JetBrains Mono', monospace" }}>{label}</span>
                {plan.goals[lvl]?.updated && <span style={{ fontSize: 10, color: "#3A3E4A" }}>· updated {new Date(plan.goals[lvl].updated).toLocaleDateString()}</span>}
              </div>
              <DeferredInput
                value={plan.goals[lvl]?.text || ""}
                onCommit={v => updateGoal(lvl, v)}
                placeholder={`What's your ${label.toLowerCase()} goal?`}
                style={{ width: "100%", background: "#1A1D23", border: `1px solid ${color}33`, borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
              />
            </div>
          ))}
        </div>

        {/* TODAY'S PLAN */}
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#6B7080", fontWeight: 700, letterSpacing: 0.8 }}>TODAY'S PLAN</div>
            <button onClick={() => generatePlan(null)} disabled={planLoading} style={{ background: planLoading ? "#1A1D23" : "linear-gradient(135deg, #00E5A0, #7C8CF8)", border: "none", borderRadius: 8, color: planLoading ? "#6B7080" : "#0B0D11", padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: planLoading ? "wait" : "pointer" }}>
              {planLoading ? "..." : plan.todayPlan?.blocks?.length ? "✦ Regenerate" : "✦ Generate Today's Plan"}
            </button>
          </div>

          {plan.todayPlan?.date === todayStr && plan.todayPlan?.blocks?.length > 0 && (
            <>
              <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
                {plan.todayPlan.blocks.map(b => (
                  <div key={b.id} style={{
                    background: b.status === "done" ? "rgba(0,229,160,0.06)" : b.status === "skipped" ? "rgba(255,107,107,0.04)" : "#1A1D23",
                    border: `1px solid ${b.status === "done" ? "rgba(0,229,160,0.3)" : b.status === "skipped" ? "rgba(255,107,107,0.2)" : "#2A2E38"}`,
                    borderRadius: 8, padding: "10px 12px",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: "#FFD166", fontFamily: "'JetBrains Mono', monospace" }}>{b.time}</div>
                      <div style={{ fontSize: 13, color: b.status === "done" ? "#00E5A0" : "#E8E9EC", textDecoration: b.status === "skipped" ? "line-through" : "none" }}>{b.label}</div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => updateBlockStatus(b.id, b.status === "done" ? "pending" : "done")} title={b.status === "done" ? "Mark pending" : "Mark done"} style={{ background: b.status === "done" ? "rgba(0,229,160,0.15)" : "transparent", border: `1px solid ${b.status === "done" ? "#00E5A0" : "#2A2E38"}`, borderRadius: 6, color: b.status === "done" ? "#00E5A0" : "#6B7080", padding: "3px 8px", fontSize: 11, cursor: "pointer" }}>✓</button>
                      <button onClick={() => updateBlockStatus(b.id, b.status === "skipped" ? "pending" : "skipped")} title="Skip" style={{ background: b.status === "skipped" ? "rgba(255,107,107,0.12)" : "transparent", border: `1px solid ${b.status === "skipped" ? "#FF6B6B" : "#2A2E38"}`, borderRadius: 6, color: b.status === "skipped" ? "#FF6B6B" : "#6B7080", padding: "3px 8px", fontSize: 11, cursor: "pointer" }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
              {plan.todayPlan.notes && <div style={{ fontSize: 12, color: "#8A8F98", padding: 10, background: "#1A1D23", borderRadius: 6, lineHeight: 1.5, marginBottom: 12 }}>{plan.todayPlan.notes}</div>}

              <div style={{ borderTop: "1px solid #1E2028", paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 6, fontWeight: 700 }}>WHAT JUST CHANGED?</div>
                <DeferredInput
                  value={whatChanged}
                  onCommit={v => setWhatChanged(v)}
                  placeholder="e.g. just got home, had to do physiotherapy, replan rest of day"
                  style={{ width: "100%", background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 8 }}
                />
                <button onClick={() => generatePlan(whatChanged)} disabled={!whatChanged.trim() || planLoading} style={{ width: "100%", background: whatChanged.trim() && !planLoading ? "rgba(124,140,248,0.12)" : "#1A1D23", border: `1px solid ${whatChanged.trim() && !planLoading ? "rgba(124,140,248,0.4)" : "#2A2E38"}`, borderRadius: 8, color: whatChanged.trim() && !planLoading ? "#7C8CF8" : "#6B7080", padding: "8px", fontSize: 12, fontWeight: 700, cursor: whatChanged.trim() && !planLoading ? "pointer" : "default" }}>
                  ↻ Replan rest of day
                </button>
              </div>
            </>
          )}
          {(!plan.todayPlan?.blocks?.length || plan.todayPlan?.date !== todayStr) && !planLoading && (
            <div style={{ fontSize: 13, color: "#6B7080", padding: "16px 0", textAlign: "center" }}>No plan yet for today. Click Generate to start.</div>
          )}
        </div>

        {/* BURNOUT PREVENTION */}
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 12, fontWeight: 700, letterSpacing: 0.8 }}>
            LIFE DOMAINS - BURNOUT PREVENTION
            {staleDomains.length > 0 && <span style={{ color: "#FF6B6B", marginLeft: 8 }}>· {staleDomains.length} stale ⚠</span>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
            {Object.entries(plan.domains).map(([key, dom]) => {
              const days = daysSince(dom.lastTouched);
              const stale = days == null || days >= STALE_DOMAIN_DAYS;
              return (
                <div key={key} style={{
                  background: stale ? "rgba(255,107,107,0.06)" : "#1A1D23",
                  border: `1px solid ${stale ? "rgba(255,107,107,0.3)" : "#2A2E38"}`,
                  borderRadius: 8, padding: 12,
                }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{dom.emoji}</div>
                  <div style={{ fontSize: 12, color: stale ? "#FF6B6B" : "#E8E9EC", fontWeight: 600 }}>{dom.label}</div>
                  <div style={{ fontSize: 11, color: stale ? "#FF6B6B" : "#6B7080", marginTop: 2 }}>
                    {dom.lastTouched ? (days === 0 ? "Today" : `${days}d ago`) : "Never touched"}
                  </div>
                  <button onClick={() => touchDomain(key)} style={{ width: "100%", background: "transparent", border: "1px solid #2A2E38", borderRadius: 6, color: "#7C8CF8", padding: "5px", fontSize: 11, cursor: "pointer", marginTop: 8 }}>Touch today</button>
                </div>
              );
            })}
          </div>
        </div>

        {/* GOAL MOVE LOG */}
        {plan.goalMoves?.length > 0 && (
          <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 10, fontWeight: 700, letterSpacing: 0.8 }}>GOAL MOVE LOG (last 10)</div>
            <div style={{ display: "grid", gap: 6 }}>
              {plan.goalMoves.slice(0, 10).map((m, i) => (
                <div key={i} style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#C4C6CE" }}>
                  <div style={{ fontSize: 10, color: "#6B7080" }}>{m.date} · {m.level}</div>
                  <div>{m.from} <span style={{ color: "#6B7080" }}>→</span> {m.to}</div>
                  {m.reason && <div style={{ fontSize: 11, color: "#8A8F98", fontStyle: "italic", marginTop: 2 }}>{m.reason}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // GETAWAYS
  // ============================================================
  const GetawaysView = () => {
    const [selectedMood, setSelectedMood] = useState(null);
    const [showView, setShowView] = useState("browse");
    const [showAddForm, setShowAddForm] = useState(false);
    const [newName, setNewName] = useState("");
    const [newType, setNewType] = useState("Nature Reset");
    const [newDist, setNewDist] = useState("");
    const [newBudget, setNewBudget] = useState("$");
    const [newBestFor, setNewBestFor] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [newCost, setNewCost] = useState("");
    const [aiLoading, setAiLoading] = useState(false);

    const MOODS = [
      { id: "Hiking",            emoji: "🥾" },
      { id: "Boating",           emoji: "🛶" },
      { id: "Adventure",         emoji: "⛰️" },
      { id: "Nature Reset",      emoji: "🌲" },
      { id: "Hot Springs",       emoji: "♨️" },
      { id: "Spiritual Retreat", emoji: "🙏" },
      { id: "Staycation",        emoji: "🏠" },
      { id: "Road Trip",         emoji: "🚗" },
      { id: "Social Recharge",   emoji: "👥" },
      { id: "Future Self",       emoji: "🌅" },
    ];

    const TYPE_COLORS = {
      "Hiking":            "#A0E89E",
      "Boating":           "#4FC3F7",
      "Adventure":         "#FFD166",
      "Nature Reset":      "#00E5A0",
      "Hot Springs":       "#7C8CF8",
      "Spiritual Retreat": "#E8A0FF",
      "Staycation":        "#6B7080",
      "Road Trip":         "#FF8C66",
      "Social Recharge":   "#FF6B6B",
      "Future Self":       "#FFC857",
    };

    const BUDGET_COLORS = { "Free": "#00E5A0", "$": "#FFD166", "$$": "#FF8C66", "$$$": "#FF6B6B" };

    const allItems = getaways.items || [];
    const activeItems = allItems.filter(g => !g.completed);
    const browsing = selectedMood ? activeItems.filter(g => g.type === selectedMood) : activeItems;
    const savedItems = activeItems.filter(g => g.saved);
    const historyItems = allItems.filter(g => g.completed);

    const displayItems = showView === "saved" ? savedItems : showView === "history" ? historyItems : browsing;

    const toggleSave = id => setGetaways({ ...getaways, items: allItems.map(g => g.id === id ? { ...g, saved: !g.saved } : g) });
    const markDone = id => setGetaways({ ...getaways, items: allItems.map(g => g.id === id ? { ...g, completed: true, completedDate: new Date().toISOString().split("T")[0] } : g) });
    const deleteItem = id => setGetaways({ ...getaways, items: allItems.filter(g => g.id !== id) });

    const addCustom = () => {
      if (!newName.trim()) return;
      const item = { id: Date.now(), name: newName.trim(), type: newType, distance: newDist.trim() || "Unknown", budget: newBudget, cost: Math.max(0, Number(newCost) || 0), bestFor: newBestFor.trim() || "Anytime", description: newDesc.trim(), saved: false, completed: false, completedDate: null, custom: true };
      setGetaways({ ...getaways, items: [item, ...allItems] });
      setNewName(""); setNewType("Hiking"); setNewDist(""); setNewBudget("$"); setNewBestFor(""); setNewDesc(""); setNewCost("");
      setShowAddForm(false);
    };

    const generateRec = async () => {
      setAiLoading(true);
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
      const recentAF = activityFeed.filter(e => e.date >= weekAgo).slice(0, 20).map(e => `[${e.date}][${e.source}] ${e.text.slice(0, 150)}`).join("\n");
      const recentQL = quickLogs.filter(l => l.date >= weekAgo).slice(0, 20).map(l => `[${l.date}] ${l.text}`).join("\n");
      const activeTasks = tasks.items.filter(t => t.status === "active").length;
      const lastJournal = journal.entries[0]?.date || "no recent entries";
      const mo = new Date().getMonth();
      const season = mo >= 2 && mo <= 4 ? "Spring" : mo >= 5 && mo <= 7 ? "Summer" : mo >= 8 && mo <= 10 ? "Fall" : "Winter";
      const systemPrompt = `You are the BlessedOps Getaway + Future-Self Advisor. The operator is Kingsley Olukanni, based in Calgary, Alberta, Canada. Founder of BlessedOps Group / Securva / Pejji. Currently in a 90-day build challenge where deep work + reset both matter.\n\nYour job: recommend ONE specific getaway, hike, paddle, or future-self activity that fits this moment. Two lenses:\n1. RESET - what kind of recharge does the recent activity pattern call for?\n2. FUTURE SELF - what activity moves him toward the life he is building? (hiking, boating, mountain confidence, social bonds, faith retreats)\n\nConsider: recent activity patterns, task load, journaling cadence (sign of self-care), current season, and the remaining budget (he's working with a starter budget cap). Be specific - name actual places, estimate drive times + cost, suggest day/time to go, and what to do there. Hike hard, paddle calm, soak slow - match the activity to the energy.\n\nEnd with: "You've earned this. Go."\n\nKeep it under 160 words. Direct and warm. No em-dashes (use hyphens or rephrase).`;
      const userPrompt = `Current season: ${season}\nMood/need selected: ${selectedMood || "not specified - give your best general recommendation"}\nActive tasks: ${activeTasks}\nLast journal entry: ${lastJournal}\nFuture-self budget: $${budgetRemaining} remaining of $${budgetCap} cap (already spent $${budgetSpent} on ${historyItems.length} completed trips)\n\nRecent Activity Feed:\n${recentAF || "None logged"}\n\nRecent Quick Logs:\n${recentQL || "None logged"}`;
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const response = await fetch("/api/claude", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${s?.access_token}` },
          body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 300, system: systemPrompt, messages: [{ role: "user", content: userPrompt }] }),
        });
        const result = await response.json();
        const text = result.content?.map(c => c.text || "").join("\n") || "Unable to generate. Try again.";
        setGetaways({ ...getaways, aiRec: { text, mood: selectedMood, generatedAt: new Date().toISOString() } });
      } catch {
        setGetaways({ ...getaways, aiRec: { text: "Connection error — try again.", mood: selectedMood, generatedAt: new Date().toISOString() } });
      }
      setAiLoading(false);
    };

    const fmtDate = d => new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    const viewBtn = (id, label) => (
      <button key={id} onClick={() => setShowView(id)} style={{ padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", background: showView === id ? "rgba(0,229,160,0.12)" : "transparent", border: `1px solid ${showView === id ? "rgba(0,229,160,0.35)" : "#1E2028"}`, color: showView === id ? "#00E5A0" : "#6B7080" }}>{label}</button>
    );

    const budgetCap = getaways.budgetCap ?? 200;
    const budgetSpent = historyItems.reduce((s, g) => s + (Number(g.cost) || 0), 0);
    const budgetRemaining = Math.max(0, budgetCap - budgetSpent);
    const budgetPct = budgetCap > 0 ? Math.min(100, (budgetSpent / budgetCap) * 100) : 0;
    const setBudgetCap = v => setGetaways({ ...getaways, budgetCap: Math.max(0, Number(v) || 0) });

    return (
      <div>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Getaways & Future Self</h2>
          <p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Reset + build the life you're working toward - hiking, boating, adventures</p>
        </div>

        {/* Budget tracker */}
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: "#6B7080", fontWeight: 700, letterSpacing: 0.8 }}>FUTURE-SELF BUDGET</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#E8E9EC", marginTop: 2 }}>
                ${budgetRemaining} <span style={{ fontSize: 13, color: "#6B7080", fontWeight: 400 }}>remaining of ${budgetCap}</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: "#6B7080" }}>Cap</span>
              <input
                type="number"
                value={budgetCap}
                onChange={e => setBudgetCap(e.target.value)}
                style={{ width: 70, background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 6, color: "#E8E9EC", padding: "5px 8px", fontSize: 12, outline: "none" }}
              />
            </div>
          </div>
          <div style={{ height: 6, background: "#1A1D23", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              width: `${budgetPct}%`, height: "100%",
              background: budgetPct >= 90 ? "#FF6B6B" : budgetPct >= 70 ? "#FFD166" : "linear-gradient(90deg, #00E5A0, #7C8CF8)",
              transition: "width 0.3s",
            }} />
          </div>
          <div style={{ fontSize: 11, color: "#6B7080", marginTop: 8 }}>
            ${budgetSpent} spent across {historyItems.length} completed {historyItems.length === 1 ? "trip" : "trips"} · cost auto-deducts when you mark done
          </div>
        </div>

        {/* Mood selector */}
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 10, fontWeight: 700, letterSpacing: 0.8 }}>WHAT DO YOU NEED?</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {MOODS.map(m => (
              <button key={m.id} onClick={() => setSelectedMood(selectedMood === m.id ? null : m.id)} style={{
                padding: "7px 14px", borderRadius: 20, fontSize: 13, cursor: "pointer",
                background: selectedMood === m.id ? `${TYPE_COLORS[m.id]}18` : "rgba(255,255,255,0.04)",
                border: `1px solid ${selectedMood === m.id ? TYPE_COLORS[m.id] : "#1E2028"}`,
                color: selectedMood === m.id ? TYPE_COLORS[m.id] : "#8A8F98",
                fontWeight: selectedMood === m.id ? 600 : 400, transition: "all 0.15s",
              }}>{m.emoji} {m.id}</button>
            ))}
          </div>
        </div>

        {/* AI Rec button + card */}
        <div style={{ marginBottom: 16 }}>
          <button onClick={generateRec} disabled={aiLoading} style={{
            width: "100%", padding: "13px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: aiLoading ? "not-allowed" : "pointer",
            background: aiLoading ? "#1A1D23" : "linear-gradient(135deg, #00E5A0, #7C8CF8)",
            border: "none", color: aiLoading ? "#6B7080" : "#0B0D11", marginBottom: getaways.aiRec ? 12 : 0,
          }}>
            {aiLoading ? "Getting your recommendation..." : "✦ Get a Personalized Recommendation"}
          </button>
          {getaways.aiRec && (
            <div style={{ background: "rgba(124,140,248,0.06)", border: "1px solid rgba(124,140,248,0.2)", borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#7C8CF8", letterSpacing: 0.5 }}>✦ AI RECOMMENDATION{getaways.aiRec.mood ? ` · ${getaways.aiRec.mood.toUpperCase()}` : ""}</div>
                <div style={{ fontSize: 11, color: "#3A3E4A" }}>{fmtDate(getaways.aiRec.generatedAt.split("T")[0])}</div>
              </div>
              <div style={{ fontSize: 14, color: "#E8E9EC", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{getaways.aiRec.text}</div>
            </div>
          )}
        </div>

        {/* View toggle + add button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 6 }}>
            {viewBtn("browse", `Browse (${activeItems.length})`)}
            {viewBtn("saved", `Saved (${savedItems.length})`)}
            {viewBtn("history", `Done (${historyItems.length})`)}
          </div>
          {showView === "browse" && (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {
                const have = new Set(allItems.map(g => g.name));
                const missing = defaultGetaways.items.filter(d => !have.has(d.name)).map(d => ({ ...d, id: Date.now() + Math.floor(Math.random() * 1e6) }));
                if (missing.length === 0) return;
                setGetaways({ ...getaways, items: [...allItems, ...missing] });
              }} title="Add any default Calgary picks not already in your list" style={{ background: "transparent", border: "1px solid #2A2E38", color: "#8A8F98", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
                Restore Calgary picks
              </button>
              <button onClick={() => setShowAddForm(v => !v)} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(0,229,160,0.1)", border: "1px solid rgba(0,229,160,0.2)", color: "#00E5A0", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
                <Icons.Plus /> Add
              </button>
            </div>
          )}
        </div>

        {/* Add form */}
        {showAddForm && (
          <div style={{ background: "#13151A", border: "1px solid rgba(0,229,160,0.2)", borderRadius: 12, padding: 18, marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#00E5A0", marginBottom: 14 }}>Add Custom Getaway</div>
            <div style={{ display: "grid", gap: 10 }}>
              <DeferredInput value={newName} onCommit={v => setNewName(v)} placeholder="Name *" style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" }} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <select value={newType} onChange={e => setNewType(e.target.value)} style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none" }}>
                  {MOODS.map(m => <option key={m.id} value={m.id}>{m.id}</option>)}
                </select>
                <select value={newBudget} onChange={e => setNewBudget(e.target.value)} style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none" }}>
                  {["Free", "$", "$$", "$$$"].map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <DeferredInput value={newDist} onCommit={v => setNewDist(v)} placeholder="Distance" style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" }} />
                <DeferredInput value={newBestFor} onCommit={v => setNewBestFor(v)} placeholder="Best for" style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" }} />
                <DeferredInput value={newCost} onCommit={v => setNewCost(v)} placeholder="Cost $ CAD" style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" }} />
              </div>
              <DeferredInput value={newDesc} onCommit={v => setNewDesc(v)} placeholder="Short description" style={{ background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" }} />
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={addCustom} style={{ flex: 1, background: newName.trim() ? "linear-gradient(135deg,#00E5A0,#7C8CF8)" : "#1A1D23", border: "none", borderRadius: 8, color: newName.trim() ? "#0B0D11" : "#6B7080", padding: "10px", fontSize: 13, fontWeight: 700, cursor: newName.trim() ? "pointer" : "default" }}>Save Getaway</button>
                <button onClick={() => setShowAddForm(false)} style={{ padding: "10px 18px", background: "transparent", border: "1px solid #2A2E38", borderRadius: 8, color: "#6B7080", fontSize: 13, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Cards */}
        {displayItems.length === 0 && (
          <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 28, textAlign: "center", color: "#6B7080", fontSize: 13 }}>
            {showView === "saved" ? "No saved getaways yet — tap ★ to save one." : showView === "history" ? "No completed getaways yet. Time to go somewhere." : selectedMood ? `No ${selectedMood} getaways in your list yet.` : "No getaways yet."}
          </div>
        )}
        <div style={{ display: "grid", gap: 12 }}>
          {displayItems.map(g => {
            const tc = TYPE_COLORS[g.type] || "#6B7080";
            const bc = BUDGET_COLORS[g.budget] || "#6B7080";
            return (
              <div key={g.id} style={{ background: "#13151A", border: `1px solid ${g.saved && !g.completed ? "rgba(255,209,102,0.25)" : "#1E2028"}`, borderRadius: 12, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#E8E9EC", flex: 1, paddingRight: 10 }}>{g.name}</div>
                  {!g.completed && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => toggleSave(g.id)} title={g.saved ? "Unsave" : "Save"} style={{ background: g.saved ? "rgba(255,209,102,0.15)" : "transparent", border: `1px solid ${g.saved ? "#FFD166" : "#2A2E38"}`, borderRadius: 7, color: g.saved ? "#FFD166" : "#6B7080", padding: "4px 10px", fontSize: 13, cursor: "pointer" }}>★</button>
                      <button onClick={() => markDone(g.id)} title="Mark done" style={{ background: "transparent", border: "1px solid #2A2E38", borderRadius: 7, color: "#6B7080", padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>✓ Done</button>
                      {g.custom && <button onClick={() => deleteItem(g.id)} style={{ background: "none", border: "none", color: "#3A3E4A", cursor: "pointer", padding: "4px 6px" }}><Icons.Trash /></button>}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 10, background: `${tc}18`, border: `1px solid ${tc}40`, color: tc, fontWeight: 600 }}>{g.type}</span>
                  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 10, background: `${bc}18`, border: `1px solid ${bc}40`, color: bc, fontWeight: 600 }}>{g.budget}</span>
                  {Number(g.cost) > 0 && <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 10, background: "rgba(255,209,102,0.12)", border: "1px solid rgba(255,209,102,0.3)", color: "#FFD166", fontWeight: 600 }}>${g.cost}</span>}
                  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid #1E2028", color: "#8A8F98" }}>📍 {g.distance}</span>
                  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid #1E2028", color: "#8A8F98" }}>🕐 {g.bestFor}</span>
                </div>
                {g.description && <div style={{ fontSize: 13, color: "#8A8F98", lineHeight: 1.5 }}>{g.description}</div>}
                {g.completed && g.completedDate && <div style={{ fontSize: 12, color: "#00E5A0", marginTop: 8 }}>✓ Completed {fmtDate(g.completedDate)}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================================
  // FAITH
  // ============================================================
  const FaithView = () => {
    const [ne, setNe] = useState(""); const [ng, setNg] = useState(""); const [nf, setNf] = useState("");
    const [scripture, setScripture] = useState(() => getScripture());
    const [showWeekLogs, setShowWeekLogs] = useState(false);
    const [reflectMode, setReflectMode] = useState("today");
    const todayStr = new Date().toISOString().split("T")[0];
    const [customStart, setCustomStart] = useState(todayStr);
    const [customEnd, setCustomEnd] = useState(todayStr);
    const [reflectLoading, setReflectLoading] = useState(false);
    const [reflectResult, setReflectResult] = useState(null);
    const [showPastReflections, setShowPastReflections] = useState(false);
    const [expandedRefl, setExpandedRefl] = useState({});

    const ae = () => { if (!ne.trim()) return; setJournal({ ...journal, entries: [{ id: Date.now(), text: ne, date: todayStr }, ...journal.entries] }); setNe(""); };
    const ag = () => { if (!ng.trim()) return; setJournal({ ...journal, gratitude: [{ id: Date.now(), text: ng, date: todayStr }, ...journal.gratitude] }); setNg(""); };
    const af = () => { if (!nf.trim()) return; setJournal({ ...journal, faithNotes: [{ id: Date.now(), text: nf, date: todayStr }, ...journal.faithNotes] }); setNf(""); };

    const weekAgoStr = new Date(Date.now() - 6 * 86400000).toISOString().split("T")[0];
    const todayLogs = quickLogs.filter(l => l.date === todayStr);
    const weekLogs = quickLogs.filter(l => l.date >= weekAgoStr);
    const displayLogs = showWeekLogs ? weekLogs : todayLogs;

    const pastReflections = reflections;

    const fmtTime = ts => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const fmtDate = d => new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    const handleGenerate = async () => {
      let dateFrom, dateTo, rangeLabel, promptType;
      if (reflectMode === "today") {
        dateFrom = dateTo = todayStr; rangeLabel = "Today's Recap"; promptType = "daily";
      } else if (reflectMode === "week") {
        dateFrom = weekAgoStr; dateTo = todayStr; rangeLabel = "This Week"; promptType = "weekly";
      } else {
        dateFrom = customStart; dateTo = customEnd;
        rangeLabel = `${fmtDate(dateFrom)} – ${fmtDate(dateTo)}`;
        const days = Math.ceil((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1;
        promptType = days <= 3 ? "daily" : days <= 14 ? "weekly" : "extended";
      }

      const logs = quickLogs.filter(l => l.date >= dateFrom && l.date <= dateTo);
      const actEntries = activityFeed.filter(e => e.date >= dateFrom && e.date <= dateTo);
      const jEntries = journal.entries.filter(e => e.date >= dateFrom && e.date <= dateTo);
      const jGratitude = journal.gratitude.filter(g => g.date >= dateFrom && g.date <= dateTo);
      const jFaith = journal.faithNotes.filter(f => f.date >= dateFrom && f.date <= dateTo);
      const doneTasks = tasks.items.filter(t => t.status === "done");
      const activeTasks = tasks.items.filter(t => t.status === "active");

      const dataBlock = `ACTIVITY FEED (${actEntries.length} entries):
${actEntries.length ? actEntries.map(e => `[${e.date}] [${e.source}]${e.title ? " " + e.title + ":" : ""} ${e.text}`).join("\n") : "None"}

QUICK LOGS (${logs.length}):
${logs.length ? logs.map(l => `[${fmtTime(l.timestamp)} ${l.date}] ${l.text}`).join("\n") : "None"}

JOURNAL ENTRIES (${jEntries.length}):
${jEntries.length ? jEntries.map(e => `[${e.date}] ${e.text}`).join("\n") : "None"}

GRATITUDE LOG (${jGratitude.length}):
${jGratitude.length ? jGratitude.map(g => `[${g.date}] ${g.text}`).join("\n") : "None"}

FAITH NOTES (${jFaith.length}):
${jFaith.length ? jFaith.map(f => `[${f.date}] ${f.text}`).join("\n") : "None"}

COMPLETED TASKS: ${doneTasks.length ? doneTasks.map(t => t.text).join(", ") : "None"}
ACTIVE TASKS: ${activeTasks.length ? activeTasks.map(t => t.text).join(", ") : "None"}
CURRENT MONTHLY INCOME: $${finances.incomeStreams.reduce((s, i) => s + (Number(i.monthly) || 0), 0).toLocaleString()}
CREDIT SCORE: ${credit.currentScore || "Not set"} (goal: ${credit.goalScore})`;

      const systemPrompt = `You are the BlessedOps Reflection Engine. You receive data from the operator's Command Center covering a specific time period.
Your job: synthesize everything into a meaningful, personal reflection. Write in second person ("You..."). Tone: like a wise friend who's been watching your week — direct, warm, faith-aware. Not robotic. Not generic.
You have access to the operator's Activity Feed — summaries from Claude AI sessions, bug bounty hunting, study sessions, workouts, business activities, and more. Use these to paint the fullest possible picture. Reference specific activities when connecting dots.`;

      let userPrompt;
      if (promptType === "daily") {
        userPrompt = `Generate a brief end-of-day recap for ${dateFrom}. Conversational, 150-250 words. Structure:

1. MOMENT — What stood out from today
2. WINS — What you accomplished or felt good about
3. INTENTION — One thing to carry into tomorrow

Data:
${dataBlock}`;
      } else if (promptType === "weekly") {
        userPrompt = `Generate a structured weekly reflection for ${dateFrom} to ${dateTo}. 400-600 words. Structure:

1. WEEK IN REVIEW — 3-4 sentences connecting the dots between the micro-moments
2. PATTERNS — What patterns appear? Energy, consistency, areas of focus or neglect
3. WINS — What went well? Call out specific entries and completed tasks
4. GROWTH EDGES — Where is there room to improve? Honest but encouraging
5. SCRIPTURE CONNECTION — One relevant verse that connects to the week's theme
6. NEXT WEEK INTENTION — One single focus for next week based on the patterns

Data:
${dataBlock}`;
      } else {
        const days = Math.ceil((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1;
        userPrompt = `Generate a reflection covering ${dateFrom} to ${dateTo} (${days} days). 400-700 words. Adapt the structure to the length of this period. Cover themes, patterns, wins, growth areas, a scripture that fits, and a forward-looking intention.

Data:
${dataBlock}`;
      }

      setReflectLoading(true);
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const response = await fetch("/api/claude", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${s?.access_token}` },
          body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1200, system: systemPrompt, messages: [{ role: "user", content: userPrompt }] }),
        });
        const result = await response.json();
        const text = result.content?.map(c => c.text || "").join("\n") || "Unable to generate reflection. Please try again.";
        const refl = { id: Date.now(), text, rangeLabel, dateFrom, dateTo, generatedAt: new Date().toISOString() };
        setReflectResult(refl);
        setReflections([refl, ...reflections]);
      } catch {
        setReflectResult({ id: Date.now(), text: "Connection error — check your internet connection and API key, then try again.", rangeLabel, dateFrom, dateTo, generatedAt: new Date().toISOString() });
      }
      setReflectLoading(false);
    };

    const modeBtn = (id, label) => (
      <button key={id} onClick={() => setReflectMode(id)} style={{
        flex: 1, padding: "9px 8px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
        background: reflectMode === id ? "rgba(124,140,248,0.15)" : "rgba(255,255,255,0.04)",
        border: `1px solid ${reflectMode === id ? "rgba(124,140,248,0.4)" : "#1E2028"}`,
        color: reflectMode === id ? "#7C8CF8" : "#6B7080",
      }}>{label}</button>
    );

    const entryRow = (e) => (
      <div key={e.id} style={{ padding: "8px 0", borderBottom: "1px solid #1A1D23", display: "flex", gap: 12 }}>
        <span style={{ fontSize: 11, color: "#6B7080", whiteSpace: "nowrap", minWidth: 75 }}>{e.date}</span>
        <span style={{ fontSize: 13, color: "#E8E9EC" }}>{e.text}</span>
      </div>
    );

    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Faith & Journal</h2>
          <p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Stewardship starts from within</p>
        </div>

        {/* Daily Scripture */}
        <div style={{ background: "linear-gradient(135deg, rgba(232,160,255,0.07), rgba(124,140,248,0.05))", border: "1px solid rgba(232,160,255,0.2)", borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#E8A0FF", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, fontWeight: 600 }}>Daily Scripture</div>
          <div style={{ fontSize: 15, color: "#E8E9EC", lineHeight: 1.75, fontStyle: "italic", marginBottom: 8 }}>"{scripture.text}"</div>
          <div style={{ fontSize: 12, color: "#E8A0FF", fontWeight: 600, marginBottom: 14 }}>— {scripture.ref}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setScripture(getScripture())} style={{ fontSize: 11, padding: "7px 14px", borderRadius: 8, background: "rgba(232,160,255,0.1)", border: "1px solid rgba(232,160,255,0.25)", color: "#E8A0FF", cursor: "pointer" }}>Refresh</button>
            <button onClick={() => setScripture(getEncouragementScripture())} style={{ fontSize: 11, padding: "7px 14px", borderRadius: 8, background: "rgba(0,229,160,0.08)", border: "1px solid rgba(0,229,160,0.2)", color: "#00E5A0", cursor: "pointer" }}>I need encouragement</button>
            <button onClick={() => setScripture(getScripture("gratitude"))} style={{ fontSize: 11, padding: "7px 14px", borderRadius: 8, background: "rgba(255,209,102,0.08)", border: "1px solid rgba(255,209,102,0.2)", color: "#FFD166", cursor: "pointer" }}>Gratitude</button>
          </div>
        </div>

        {/* Quick Log Card */}
        <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#00E5A0" }}>⚡ Quick Log</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowWeekLogs(false)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, background: !showWeekLogs ? "rgba(0,229,160,0.12)" : "transparent", border: `1px solid ${!showWeekLogs ? "rgba(0,229,160,0.3)" : "#1E2028"}`, color: !showWeekLogs ? "#00E5A0" : "#6B7080", cursor: "pointer" }}>Today</button>
              <button onClick={() => setShowWeekLogs(true)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, background: showWeekLogs ? "rgba(0,229,160,0.12)" : "transparent", border: `1px solid ${showWeekLogs ? "rgba(0,229,160,0.3)" : "#1E2028"}`, color: showWeekLogs ? "#00E5A0" : "#6B7080", cursor: "pointer" }}>This Week</button>
            </div>
          </div>
          {displayLogs.length === 0 ? (
            <div style={{ fontSize: 13, color: "#3A3E4A", textAlign: "center", padding: "12px 0" }}>
              {showWeekLogs ? "No logs this week" : "No logs today — tap ⚡ to add one"}
            </div>
          ) : (
            <div>
              {displayLogs.map(l => (
                <div key={l.id} style={{ padding: "7px 0", borderBottom: "1px solid #1A1D23", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 11, color: "#6B7080", whiteSpace: "nowrap", fontFamily: "'JetBrains Mono', monospace", minWidth: showWeekLogs ? 90 : 44 }}>
                    {showWeekLogs ? `${l.date.slice(5)} ` : ""}{fmtTime(l.timestamp)}
                  </span>
                  <span style={{ fontSize: 13, color: "#E8E9EC", lineHeight: 1.4 }}>{l.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Journal cards */}
        <div style={{ display: "grid", gap: 16, marginBottom: 20 }}>
          <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
            <SectionHeader title="Daily Journal" />
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <input value={ne} onChange={e => setNe(e.target.value)} onKeyDown={e => e.key === "Enter" && ae()} placeholder="What happened today?" style={{ flex: 1, background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 8, color: "#E8E9EC", padding: "10px 14px", fontSize: 13, outline: "none" }} />
              <button onClick={ae} style={{ background: "#00E5A0", color: "#0B0D11", border: "none", borderRadius: 8, padding: "0 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Save</button>
            </div>
            <div>{journal.entries.slice(0, 10).map(entryRow)}{journal.entries.length === 0 && <div style={{ fontSize: 13, color: "#6B7080", padding: 8 }}>No entries yet</div>}</div>
          </div>
          <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
            <SectionHeader title="Gratitude Log" />
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <input value={ng} onChange={e => setNg(e.target.value)} onKeyDown={e => e.key === "Enter" && ag()} placeholder="What are you grateful for?" style={{ flex: 1, background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 8, color: "#E8E9EC", padding: "10px 14px", fontSize: 13, outline: "none" }} />
              <button onClick={ag} style={{ background: "#FFD166", color: "#0B0D11", border: "none", borderRadius: 8, padding: "0 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Save</button>
            </div>
            <div>{journal.gratitude.slice(0, 10).map(entryRow)}{journal.gratitude.length === 0 && <div style={{ fontSize: 13, color: "#6B7080", padding: 8 }}>No entries yet</div>}</div>
          </div>
          <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 24 }}>
            <SectionHeader title="Faith Notes" />
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <input value={nf} onChange={e => setNf(e.target.value)} onKeyDown={e => e.key === "Enter" && af()} placeholder="Scripture, reflections, prayers..." style={{ flex: 1, background: "#1A1D23", border: "1px solid #3A3E4A", borderRadius: 8, color: "#E8E9EC", padding: "10px 14px", fontSize: 13, outline: "none" }} />
              <button onClick={af} style={{ background: "#E8A0FF", color: "#0B0D11", border: "none", borderRadius: 8, padding: "0 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Save</button>
            </div>
            <div>{journal.faithNotes.slice(0, 10).map(entryRow)}{journal.faithNotes.length === 0 && <div style={{ fontSize: 13, color: "#6B7080", padding: 8 }}>No entries yet</div>}</div>
          </div>
        </div>

        {/* AI Reflection */}
        <div style={{ background: "#13151A", border: "1px solid rgba(124,140,248,0.2)", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#7C8CF8", marginBottom: 4 }}>AI Reflection Engine</div>
          <div style={{ fontSize: 12, color: "#6B7080", marginBottom: 16 }}>Synthesizes your logs, journal, and data into a personal reflection</div>

          {/* Mode selector */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {modeBtn("today", "Today")}
            {modeBtn("week", "This Week")}
            {modeBtn("custom", "Custom Range")}
          </div>

          {/* Custom date pickers */}
          {reflectMode === "custom" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 4 }}>From</div>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} colorScheme="dark" style={{ width: "100%", background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 4 }}>To</div>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} colorScheme="dark" style={{ width: "100%", background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>
          )}

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={reflectLoading}
            style={{
              width: "100%", padding: "12px", borderRadius: 10, border: "none", cursor: reflectLoading ? "not-allowed" : "pointer",
              background: reflectLoading ? "#1A1D23" : "linear-gradient(135deg, rgba(124,140,248,0.2), rgba(0,229,160,0.15))",
              border: "1px solid rgba(124,140,248,0.3)",
              color: reflectLoading ? "#6B7080" : "#7C8CF8", fontSize: 13, fontWeight: 700, marginBottom: reflectResult ? 20 : 0,
            }}
          >{reflectLoading ? "Generating reflection..." : `Generate ${reflectMode === "today" ? "Today's Recap" : reflectMode === "week" ? "Weekly Reflection" : "Reflection"}`}</button>

          {/* Current result */}
          {reflectResult && (
            <div style={{ background: "rgba(124,140,248,0.05)", border: "1px solid rgba(124,140,248,0.15)", borderRadius: 10, padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#7C8CF8" }}>{reflectResult.rangeLabel}</div>
                <div style={{ fontSize: 11, color: "#3A3E4A" }}>{new Date(reflectResult.generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <div style={{ fontSize: 13, color: "#C8CAD0", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{reflectResult.text}</div>
            </div>
          )}

          {/* Past reflections */}
          {pastReflections.length > 1 && (
            <div style={{ marginTop: 16 }}>
              <button onClick={() => setShowPastReflections(v => !v)} style={{ background: "none", border: "none", color: "#6B7080", fontSize: 12, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6 }}>
                {showPastReflections ? "▲" : "▼"} {pastReflections.length - 1} past reflection{pastReflections.length - 1 !== 1 ? "s" : ""}
              </button>
              {showPastReflections && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {pastReflections.slice(1).map(r => (
                    <div key={r.id} style={{ background: "#0E1015", border: "1px solid #1E2028", borderRadius: 8, overflow: "hidden" }}>
                      <button onClick={() => setExpandedRefl(prev => ({ ...prev, [r.id]: !prev[r.id] }))} style={{ width: "100%", background: "none", border: "none", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#7C8CF8" }}>{r.rangeLabel}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 11, color: "#3A3E4A" }}>{new Date(r.generatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                          <span style={{ fontSize: 11, color: "#6B7080" }}>{expandedRefl[r.id] ? "▲" : "▼"}</span>
                        </div>
                      </button>
                      {expandedRefl[r.id] && (
                        <div style={{ padding: "0 14px 14px", fontSize: 13, color: "#8A8F98", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{r.text}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // NETWORK & CRM
  // ============================================================
  const NetworkView = () => {
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState("");
    const [filterType, setFilterType] = useState("all");
    const emptyContact = { name: "", role: "", company: "", location: "", email: "", phone: "", type: "Professional", strength: "New", howMet: "", lastContact: new Date().toISOString().split("T")[0], tags: "", notes: "" };
    const [form, setForm] = useState({ ...emptyContact });

    const saveContact = () => {
      if (!form.name.trim()) return;
      if (editingId) {
        setNetwork({ ...network, contacts: network.contacts.map(c => c.id === editingId ? { ...form, id: editingId } : c) });
      } else {
        setNetwork({ ...network, contacts: [{ ...form, id: Date.now() }, ...network.contacts] });
      }
      setForm({ ...emptyContact });
      setShowForm(false);
      setEditingId(null);
    };

    const editContact = (c) => {
      setForm({ ...c });
      setEditingId(c.id);
      setShowForm(true);
    };

    const removeContact = (id) => {
      setNetwork({ ...network, contacts: network.contacts.filter(c => c.id !== id) });
      if (editingId === id) { setShowForm(false); setEditingId(null); }
    };

    const getDaysSince = (lastContact) => lastContact ? Math.floor((Date.now() - new Date(lastContact).getTime()) / 86400000) : null;

    const getSuggestedAction = (c) => {
      const actions = {
        "Professional": "Send a quick email or LinkedIn message to check in on their current projects.",
        "Client": "Reach out to see if they have any security needs or questions since your last conversation.",
        "Mentor": "Share a quick update on your Security+ progress and ask if they have 15 minutes to connect.",
        "Personal": "Send a voice message or short text — just let them know you're thinking of them.",
        "Church/Faith": "Invite them to connect this weekend or share something you've been reflecting on.",
      };
      return actions[c.type] || "Send a brief check-in message and ask how things are going.";
    };

    const filtered = network.contacts
      .filter(c => {
        const q = search.toLowerCase();
        const matchSearch = !q || c.name?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.role?.toLowerCase().includes(q) || c.tags?.toLowerCase().includes(q);
        const matchType = filterType === "all" || filterType === "followup"
          ? filterType !== "followup" || (getDaysSince(c.lastContact) !== null && getDaysSince(c.lastContact) > 30)
          : c.type === filterType;
        return matchSearch && matchType;
      })
      .sort((a, b) => {
        const da = getDaysSince(a.lastContact) ?? -1;
        const db = getDaysSince(b.lastContact) ?? -1;
        return db - da;
      });

    const strengthColors = { "Strong": "#00E5A0", "Good": "#7C8CF8", "Warm": "#FFD166", "New": "#6B7080" };
    const typeColors = { "Professional": "#7C8CF8", "Personal": "#00E5A0", "Church/Faith": "#E8A0FF", "Mentor": "#FFD166", "Client": "#FF8C66" };

    const formFieldStyle = { width: "100%", background: "#1A1D23", border: "1px solid #2A2E38", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 13, outline: "none", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" };
    const labelStyle = { fontSize: 11, fontWeight: 600, color: "#8A8F98", marginBottom: 6, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };

    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Network & CRM</h2>
          <p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Build and maintain meaningful relationships</p>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
          <StatCard label="Total" value={network.contacts.length} color="#7C8CF8" />
          <StatCard label="Strong" value={network.contacts.filter(c => c.strength === "Strong").length} color="#00E5A0" />
          <StatCard label="Follow Up" value={network.contacts.filter(c => { if (!c.lastContact) return false; const d = (Date.now() - new Date(c.lastContact).getTime()) / 86400000; return d > 30; }).length} sub="30+ days" color="#FFD166" />
        </div>

        {/* Search + Filter + Add */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..." style={{ flex: 1, minWidth: 150, background: "#13151A", border: "1px solid #1E2028", borderRadius: 8, color: "#E8E9EC", padding: "10px 14px", fontSize: 13, outline: "none" }} />
          <select value={filterType === "followup" ? "all" : filterType} onChange={e => setFilterType(e.target.value)} style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 8, color: "#E8E9EC", padding: "10px 12px", fontSize: 12, outline: "none" }}>
            <option value="all">All Types</option>
            <option value="Professional">Professional</option>
            <option value="Personal">Personal</option>
            <option value="Church/Faith">Church/Faith</option>
            <option value="Mentor">Mentor</option>
            <option value="Client">Client</option>
          </select>
          <button onClick={() => setFilterType(filterType === "followup" ? "all" : "followup")} style={{ background: filterType === "followup" ? "rgba(255,209,102,0.15)" : "rgba(255,209,102,0.05)", border: `1px solid ${filterType === "followup" ? "rgba(255,209,102,0.5)" : "rgba(255,209,102,0.15)"}`, color: "#FFD166", borderRadius: 8, padding: "10px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
            Needs Follow-Up
          </button>
          <button onClick={() => { setForm({ ...emptyContact }); setEditingId(null); setShowForm(!showForm); }} style={{ background: showForm ? "rgba(255,107,107,0.15)" : "rgba(0,229,160,0.1)", border: `1px solid ${showForm ? "rgba(255,107,107,0.3)" : "rgba(0,229,160,0.2)"}`, color: showForm ? "#FF6B6B" : "#00E5A0", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            {showForm ? "Cancel" : <><Icons.Plus /> Add Contact</>}
          </button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#E8E9EC", margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>
              <Icons.Network /> {editingId ? "Edit Contact" : "Add New Contact"}
            </h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><label style={labelStyle}>Full Name</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., John Smith" style={formFieldStyle} /></div>
              <div><label style={labelStyle}>Title / Role</label><input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g., CEO, Developer, Pastor" style={formFieldStyle} /></div>
              <div><label style={labelStyle}>Company / Organization</label><input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="e.g., Tech Corp, Local Church" style={formFieldStyle} /></div>
              <div><label style={labelStyle}>Location</label><input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g., New York, USA" style={formFieldStyle} /></div>
              <div><label style={labelStyle}>Email</label><input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" type="email" style={formFieldStyle} /></div>
              <div><label style={labelStyle}>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 234 567 8900" type="tel" style={formFieldStyle} /></div>
              <div>
                <label style={labelStyle}>Relationship Type</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={formFieldStyle}>
                  <option value="Professional">Professional</option>
                  <option value="Personal">Personal</option>
                  <option value="Church/Faith">Church/Faith</option>
                  <option value="Mentor">Mentor</option>
                  <option value="Client">Client</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Connection Strength</label>
                <select value={form.strength} onChange={e => setForm({ ...form, strength: e.target.value })} style={formFieldStyle}>
                  <option value="Strong">Strong</option>
                  <option value="Good">Good</option>
                  <option value="Warm">Warm</option>
                  <option value="New">New</option>
                </select>
              </div>
              <div><label style={labelStyle}>How We Met</label><input value={form.howMet} onChange={e => setForm({ ...form, howMet: e.target.value })} placeholder="e.g., Conference, Church, LinkedIn" style={formFieldStyle} /></div>
              <div><label style={labelStyle}>Last Contact Date</label><input value={form.lastContact} onChange={e => setForm({ ...form, lastContact: e.target.value })} type="date" style={{ ...formFieldStyle, colorScheme: "dark" }} /></div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Tags</label>
              <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="e.g., tech, leadership, startup (comma-separated)" style={formFieldStyle} />
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Personal notes, interests, family info, business opportunities..." rows={3} style={{ ...formFieldStyle, resize: "vertical" }} />
            </div>

            <button onClick={saveContact} disabled={!form.name.trim()} style={{ marginTop: 16, width: "100%", background: form.name.trim() ? "linear-gradient(135deg, #00E5A0, #7C8CF8)" : "#3A3E4A", border: "none", borderRadius: 10, padding: "12px", color: "#0B0D11", fontSize: 14, fontWeight: 700, cursor: form.name.trim() ? "pointer" : "not-allowed" }}>
              {editingId ? "Save Changes" : "Add Contact"}
            </button>
          </div>
        )}

        {/* Contact Cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.length === 0 && !showForm && (
            <div style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: 32, textAlign: "center" }}>
              <div style={{ fontSize: 14, color: "#6B7080", marginBottom: 8 }}>{network.contacts.length === 0 ? "No contacts yet" : "No matches found"}</div>
              <div style={{ fontSize: 12, color: "#3A3E4A" }}>{network.contacts.length === 0 ? "Start building your network — tap Add Contact above" : "Try a different search or filter"}</div>
            </div>
          )}

          {filtered.map(c => {
            const daysSince = getDaysSince(c.lastContact);
            const needsFollowUp = daysSince !== null && daysSince > 30;
            return (
              <div key={c.id} style={{ background: "#13151A", border: `1px solid ${needsFollowUp ? "rgba(255,209,102,0.3)" : "#1E2028"}`, borderRadius: 12, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#E8E9EC" }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#6B7080", marginTop: 2 }}>
                      {[c.role, c.company].filter(Boolean).join(" · ") || "No details yet"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button onClick={() => editContact(c)} style={{ background: "none", border: "none", color: "#6B7080", cursor: "pointer", padding: 4 }}><Icons.Arrow /></button>
                    <button onClick={() => removeContact(c.id)} style={{ background: "none", border: "none", color: "#3A3E4A", cursor: "pointer", padding: 4 }}><Icons.Trash /></button>
                  </div>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 10, background: `${typeColors[c.type] || "#6B7080"}18`, color: typeColors[c.type] || "#6B7080", border: `1px solid ${typeColors[c.type] || "#6B7080"}30` }}>{c.type}</span>
                  <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 10, background: `${strengthColors[c.strength] || "#6B7080"}18`, color: strengthColors[c.strength] || "#6B7080", border: `1px solid ${strengthColors[c.strength] || "#6B7080"}30` }}>{c.strength}</span>
                  {needsFollowUp && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 10, background: "rgba(255,209,102,0.15)", color: "#FFD166", border: "1px solid rgba(255,209,102,0.3)" }}>{daysSince}d ago — follow up</span>}
                  {c.tags && c.tags.split(",").map((t, i) => (
                    <span key={i} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 10, background: "rgba(255,255,255,0.05)", color: "#8A8F98" }}>{t.trim()}</span>
                  ))}
                </div>

                {(c.email || c.phone || c.location) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 11, color: "#6B7080", marginBottom: 6 }}>
                    {c.email && <span>{c.email}</span>}
                    {c.phone && <span>{c.phone}</span>}
                    {c.location && <span>{c.location}</span>}
                  </div>
                )}

                {c.howMet && <div style={{ fontSize: 11, color: "#6B7080" }}>Met: {c.howMet}</div>}
                {c.notes && <div style={{ fontSize: 12, color: "#8A8F98", marginTop: 6, lineHeight: 1.5, borderTop: "1px solid #1A1D23", paddingTop: 8 }}>{c.notes}</div>}
                {needsFollowUp && <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(255,209,102,0.06)", border: "1px solid rgba(255,209,102,0.15)", borderRadius: 8, fontSize: 12, color: "#FFD166", lineHeight: 1.5 }}>Suggested: {getSuggestedAction(c)}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================================
  // ============================================================
  // ACTIVITY FEED VIEW
  // ============================================================
  const ActivityFeedView = () => {
    const [search, setSearch] = useState("");
    const [filterSource, setFilterSource] = useState("all");
    const [filterRange, setFilterRange] = useState("all");

    const allEntries = activityFeed;
    const todayStr = new Date().toISOString().split("T")[0];
    const weekAgoStr = new Date(Date.now() - 6 * 86400000).toISOString().split("T")[0];

    const filtered = allEntries.filter(e => {
      const q = search.toLowerCase();
      const matchSearch = !q || e.text?.toLowerCase().includes(q) || e.title?.toLowerCase().includes(q) || e.source?.toLowerCase().includes(q);
      const matchSource = filterSource === "all" || e.source === filterSource;
      const matchRange = filterRange === "all" || (filterRange === "today" && e.date === todayStr) || (filterRange === "week" && e.date >= weekAgoStr);
      return matchSearch && matchSource && matchRange;
    });

    const fmtTime = ts => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const rangeBtn = (id, label) => (
      <button key={id} onClick={() => setFilterRange(id)} style={{ padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", background: filterRange === id ? "rgba(124,140,248,0.15)" : "transparent", border: `1px solid ${filterRange === id ? "rgba(124,140,248,0.35)" : "#1E2028"}`, color: filterRange === id ? "#7C8CF8" : "#6B7080" }}>{label}</button>
    );

    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9EC", margin: 0 }}>Activity Feed</h2>
          <p style={{ fontSize: 13, color: "#6B7080", margin: "4px 0 0" }}>Everything you've been doing — unified context for the AI</p>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
          <StatCard label="Total" value={allEntries.length} color="#7C8CF8" />
          <StatCard label="This Week" value={allEntries.filter(e => e.date >= weekAgoStr).length} color="#00E5A0" />
          <StatCard label="Today" value={allEntries.filter(e => e.date === todayStr).length} color="#FFD166" />
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          {[rangeBtn("all", "All Time"), rangeBtn("week", "This Week"), rangeBtn("today", "Today")]}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search entries..."
            style={{ flex: 1, minWidth: 160, background: "#13151A", border: "1px solid #1E2028", borderRadius: 8, color: "#E8E9EC", padding: "9px 14px", fontSize: 13, outline: "none" }}
          />
          <select
            value={filterSource}
            onChange={e => setFilterSource(e.target.value)}
            style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 8, color: "#E8E9EC", padding: "9px 12px", fontSize: 12, outline: "none" }}
          >
            <option value="all">All Sources</option>
            {ALL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Add entry prompt */}
        <div style={{ background: "rgba(124,140,248,0.05)", border: "1px solid rgba(124,140,248,0.15)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#6B7080" }}>
          Tap <span style={{ color: "#7C8CF8", fontWeight: 600 }}>⚡</span> → <span style={{ color: "#7C8CF8", fontWeight: 600 }}>Activity Summary</span> (bottom-right) to log any session, workout, or work block.
        </div>

        {/* Entries */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#3A3E4A", fontSize: 14 }}>
            {allEntries.length === 0 ? "No activity logged yet. Start adding summaries to build your context feed." : "No entries match your filters."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map(e => {
              const color = SOURCE_COLORS[e.source] || "#6B7080";
              return (
                <div key={e.id} style={{ background: "#13151A", border: "1px solid #1E2028", borderRadius: 10, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: color + "18", border: `1px solid ${color}33`, color }}>{e.source}</span>
                      {e.title && <span style={{ fontSize: 13, fontWeight: 600, color: "#E8E9EC" }}>{e.title}</span>}
                    </div>
                    <span style={{ fontSize: 11, color: "#3A3E4A", whiteSpace: "nowrap", fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>{e.date} {fmtTime(e.timestamp)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#9A9FA8", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{e.text}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // RENDER
  // ============================================================
  const ProfileView = () => {
    const [resetSent, setResetSent] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const handleChangePassword = async () => {
      setResetLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(session.user.email, {
        redirectTo: window.location.origin,
      });
      if (!error) setResetSent(true);
      setResetLoading(false);
    };

    const joinedDate = session?.user?.created_at
      ? new Date(session.user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : null;

    const card = { background: "#13151A", border: "1px solid #1E2028", borderRadius: 12, padding: "20px" };
    const label = { fontSize: 11, color: "#6B7080", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 };
    const value = { fontSize: 14, color: "#E8E9EC", fontWeight: 500, wordBreak: "break-all" };
    const divider = { borderTop: "1px solid #1E2028", margin: "16px 0" };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Header */}
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#E8E9EC", marginBottom: 4 }}>Account & Profile</div>
          <div style={{ fontSize: 13, color: "#6B7080" }}>Manage your BlessedOps account</div>
        </div>

        {/* Account Info */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg, #00E5A0, #7C8CF8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: "#0B0D11", flexShrink: 0 }}>
              {(session?.user?.email?.[0] || "U").toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#E8E9EC" }}>{session?.user?.email}</div>
              <div style={{ fontSize: 12, color: "#00E5A0", marginTop: 2 }}>Active account</div>
            </div>
          </div>

          <div style={divider} />

          <div style={{ marginBottom: 14 }}>
            <div style={label}>Email</div>
            <div style={value}>{session?.user?.email}</div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={label}>User ID</div>
            <div style={{ ...value, fontSize: 12, color: "#6B7080", fontFamily: "'JetBrains Mono', monospace" }}>{session?.user?.id}</div>
          </div>

          {joinedDate && (
            <div>
              <div style={label}>Member Since</div>
              <div style={value}>{joinedDate}</div>
            </div>
          )}
        </div>

        {/* Security */}
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#E8E9EC", marginBottom: 16 }}>Security</div>

          {resetSent ? (
            <div style={{ background: "rgba(0,229,160,0.08)", border: "1px solid rgba(0,229,160,0.25)", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#00E5A0", lineHeight: 1.5 }}>
              Password reset email sent to {session?.user?.email}. Check your inbox and follow the link.
            </div>
          ) : (
            <button
              onClick={handleChangePassword}
              disabled={resetLoading}
              style={{ width: "100%", background: "rgba(124,140,248,0.08)", border: "1px solid rgba(124,140,248,0.25)", borderRadius: 8, color: "#7C8CF8", padding: "12px 16px", fontSize: 13, fontWeight: 600, cursor: resetLoading ? "not-allowed" : "pointer", textAlign: "left" }}
            >
              {resetLoading ? "Sending..." : "Change Password — send reset email"}
            </button>
          )}
        </div>

        {/* Data Sync */}
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#E8E9EC", marginBottom: 8 }}>Data Sync</div>
          <div style={{ fontSize: 13, color: "#6B7080", lineHeight: 1.6 }}>
            All your data syncs to Supabase cloud storage. Changes save automatically within 1 second and are available across all your devices.
          </div>
        </div>

        {/* Sign Out */}
        <button
          onClick={() => supabase.auth.signOut()}
          style={{ width: "100%", background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 10, color: "#FF6B6B", padding: "14px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
        >
          Sign Out
        </button>
      </div>
    );
  };

  const renderView = () => {
    switch (activeTab) {
      case "dashboard": return <DashboardView />;
      case "advisor": return <AdvisorView />;
      case "activity": return <ActivityFeedView />;
      case "money": return <MoneyView />;
      case "credit": return <CreditView />;
      case "tasks": return <TasksView />;
      case "learning": return <LearningView />;
      case "content": return <ContentView />;
      case "getaways": return <GetawaysView />;
      case "plan": return <PlanView />;
      case "faith": return <FaithView />;
      case "network": return <NetworkView />;
      case "profile": return <ProfileView />;
      default: return <DashboardView />;
    }
  };

  const MenuIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
  );
  const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#0B0D11", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: "#E8E9EC", position: "relative" }}>
      <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @media (min-width: 640px) {
          .bo-grid-2 { grid-template-columns: 1fr 1fr !important; }
          .bo-grid-3 { grid-template-columns: 1fr 1fr 1fr !important; }
        }
        * { box-sizing: border-box; }
      `}</style>

      {/* Overlay when sidebar is open (click to close) */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40, backdropFilter: "blur(2px)" }} />
      )}

      {/* Sidebar - slides in/out */}
      <div style={{
        position: "fixed", top: 0, left: 0, bottom: 0,
        width: 240, background: "#0E1015", borderRight: "1px solid #1A1D23",
        transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: 50, display: "flex", flexDirection: "column",
        boxShadow: sidebarOpen ? "4px 0 24px rgba(0,0,0,0.4)" : "none"
      }}>
        {/* Sidebar Header */}
        <div style={{ paddingTop: "max(16px, env(safe-area-inset-top, 16px))", paddingBottom: "12px", paddingLeft: "16px", paddingRight: "16px", borderBottom: "1px solid #1A1D23", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#00E5A0", fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>BLESSEDOPS</div>
            <div style={{ fontSize: 10, color: "#6B7080", marginTop: 2 }}>Command Center v2</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} style={{ background: "none", border: "none", color: "#6B7080", cursor: "pointer", padding: 4, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CloseIcon />
          </button>
        </div>

        {/* Nav Items */}
        <div style={{ padding: "12px 8px", flex: 1, overflowY: "auto" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setActiveTab(t.id); setSidebarOpen(false); }} style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 12px",
              background: activeTab === t.id ? (t.id === "advisor" ? "rgba(124,140,248,0.1)" : "rgba(0,229,160,0.08)") : "transparent",
              border: t.id === "advisor" && activeTab === t.id ? "1px solid rgba(124,140,248,0.3)" : "1px solid transparent",
              borderRadius: 8, color: activeTab === t.id ? (t.id === "advisor" ? "#7C8CF8" : "#00E5A0") : "#6B7080",
              cursor: "pointer", fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400, marginBottom: 2,
              transition: "all 0.15s ease", whiteSpace: "nowrap", textAlign: "left"
            }}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: "12px 16px", borderTop: "1px solid #1A1D23" }}>
          <div style={{ fontSize: 10, color: "#3A3E4A", marginBottom: 10 }}>
            Systems over hustle.<br />Document → Refine → Package.
          </div>
          {session?.user?.email && (
            <div style={{ fontSize: 11, color: "#6B7080", marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {session.user.email}
            </div>
          )}
          <button onClick={() => supabase.auth.signOut()} style={{ width: "100%", background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 8, color: "#FF6B6B", padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "center" }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {/* Top Bar with hamburger - safe area aware */}
        <div style={{
          position: "sticky", top: 0, zIndex: 30,
          background: "#0B0D11", borderBottom: "1px solid #1A1D23",
          paddingTop: "max(16px, env(safe-area-inset-top, 16px))",
          paddingBottom: "12px", paddingLeft: "16px", paddingRight: "16px",
          display: "flex", alignItems: "center", gap: 12
        }}>
          <button onClick={() => setSidebarOpen(true)} style={{
            background: "rgba(0,229,160,0.08)", border: "1px solid rgba(0,229,160,0.15)",
            borderRadius: 8, padding: "10px 12px", cursor: "pointer", color: "#00E5A0",
            display: "flex", alignItems: "center", justifyContent: "center",
            minWidth: 44, minHeight: 44
          }}>
            <MenuIcon />
          </button>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#E8E9EC" }}>
            {tabs.find(t => t.id === activeTab)?.label || "Command Center"}
          </div>
        </div>

        {/* Page Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 12px max(20px, env(safe-area-inset-bottom, 20px))", maxWidth: 900, width: "100%", margin: "0 auto" }}>
          {renderView()}
        </div>
      </div>

      {/* Overlays */}
      <QuickLogOverlay isOpen={showQuickLog} onClose={() => setShowQuickLog(false)} quickLogs={quickLogs} setQuickLogs={setQuickLogs} />
      <ActivitySummaryOverlay isOpen={showActivitySummary} onClose={() => setShowActivitySummary(false)} activityFeed={activityFeed} setActivityFeed={setActivityFeed} />

      {/* Quick action menu backdrop */}
      {quickActionMenu && (
        <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setQuickActionMenu(false)} />
      )}

      {/* Quick action popup menu */}
      {!sidebarOpen && !showMindMap && quickActionMenu && (
        <div style={{
          position: "fixed", bottom: 144, right: 12, zIndex: 101,
          background: "#13151A", border: "1px solid #2A2E38", borderRadius: 12,
          padding: 8, display: "flex", flexDirection: "column", gap: 5,
          boxShadow: "0 4px 24px rgba(0,0,0,0.55)",
        }}>
          <button
            onClick={() => { setShowQuickLog(true); setQuickActionMenu(false); }}
            style={{ background: "rgba(0,229,160,0.08)", border: "1px solid rgba(0,229,160,0.2)", borderRadius: 8, color: "#00E5A0", padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            Quick Log
          </button>
          <button
            onClick={() => { setShowActivitySummary(true); setQuickActionMenu(false); }}
            style={{ background: "rgba(124,140,248,0.08)", border: "1px solid rgba(124,140,248,0.2)", borderRadius: 8, color: "#7C8CF8", padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            Activity Summary
          </button>
        </div>
      )}

      {/* ⚡ floating button — toggles quick action menu */}
      {!sidebarOpen && !showMindMap && (
        <button
          onClick={() => setQuickActionMenu(v => !v)}
          style={{
            position: "fixed", bottom: 88, right: 20, zIndex: 100,
            width: 44, height: 44, borderRadius: "50%",
            background: quickActionMenu ? "rgba(0,229,160,0.2)" : "rgba(0,229,160,0.10)",
            border: `1.5px solid ${quickActionMenu ? "rgba(0,229,160,0.6)" : "rgba(0,229,160,0.3)"}`,
            boxShadow: quickActionMenu ? "0 0 16px rgba(0,229,160,0.35)" : "0 0 10px rgba(0,229,160,0.15)",
            cursor: "pointer", color: "#00E5A0",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.15s",
          }}
          title="Quick actions"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
          </svg>
        </button>
      )}

      {/* Floating Mind Map trigger — hidden when sidebar open */}
      {!sidebarOpen && !showMindMap && (
        <button
          onClick={() => setShowMindMap(true)}
          style={{
            position: "fixed", bottom: 24, right: 20, zIndex: 100,
            width: 52, height: 52, borderRadius: "50%",
            background: "radial-gradient(ellipse at 40% 30%, #7c1d1d 0%, #3d0e0e 55%, #1a0606 100%)",
            border: "1.5px solid #991b1b",
            boxShadow: "0 0 18px rgba(153,27,27,0.45)",
            cursor: "pointer", color: "#f5c6c6",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          title="BlessedOps Group Structure"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L3 7v6c0 5.25 3.75 10.05 9 11.25C17.25 23.05 21 18.25 21 13V7L12 2z"/>
          </svg>
        </button>
      )}

      {/* Mind Map full-screen overlay */}
      {showMindMap && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#060606", display: "flex", flexDirection: "column" }}>
          {/* Overlay top bar */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, zIndex: 201,
            paddingTop: "max(14px, env(safe-area-inset-top, 14px))",
            paddingBottom: 12, paddingLeft: 14, paddingRight: 14,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "linear-gradient(to bottom, rgba(6,6,6,0.95) 70%, transparent)",
            pointerEvents: "none",
          }}>
            <div style={{ pointerEvents: "auto" }}>
              <button
                onClick={() => setShowMindMap(false)}
                style={{
                  background: "rgba(255,255,255,0.06)", border: "1px solid #2A2E38",
                  borderRadius: 8, color: "#C8CAD0", padding: "8px 16px",
                  fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                Back
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#3A3E4A", fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1, pointerEvents: "none" }}>
              BLESSEDOPS GROUP · STRUCTURE
            </div>
            <div style={{ width: 80 }} />
          </div>
          <BlessedOpsMindMapDraggable />
        </div>
      )}
    </div>
  );
}
